import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Circle,
  BookOpen,
  Clock,
  Target,
  Users,
  Award,
  MessageCircle,
  X,
  Send,
  Sparkles,
  Brain,
  Loader2,
  Home,
  HelpCircle,
  Trophy,
  Upload,
  Bot,
  Edit3,
  Image as ImageIcon,
  Save,
  Eye,
  Rocket,
  PenLine,
  Plus,
  Trash2,
  GripVertical,
  Crop,
  Move,
  ListOrdered,
  CheckSquare,
  FileQuestion,
  Wand2,
  Grid,
  Search,
  Video,
  Maximize2,
  Minimize2,
  Beaker,
  FileText,
  Download,
  File
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../contexts/AuthContext';
import { Progress } from '../components/ui/progress';
import ReactMarkdown from 'react-markdown';
import NaviCoach from '../components/NaviCoach';
import NaviAssessment from '../components/NaviAssessment';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import ImageCropper, { FocalPointSelector } from '../components/ImageCropper';
import RichTextEditor from '../components/RichTextEditor';
import CourseImageManager from '../components/admin/CourseImageManager';
import { API_BASE } from '../config/api';
import NextActivityCard from '../components/programme/NextActivityCard';
import VisualBlocks from '../components/course/VisualBlocks';
import EnhanceVisualsButton from '../components/course/EnhanceVisualsButton';
import SavedVisualsEditor from '../components/course/SavedVisualsEditor';
import { CourseReviewBanner, SectionSources, SectionFactFlags } from '../components/course/CourseFactCheck';
import EnhanceAllVisualsButton from '../components/course/EnhanceAllVisualsButton';
import CourseGeneratingScreen from '../components/course/CourseGeneratingScreen';
import SectionMediaUploader from '../components/course/SectionMediaUploader';
import EmbeddedMediaGrid from '../components/course/EmbeddedMediaGrid';
import GenerateAllImagesButton from '../components/course/GenerateAllImagesButton';
import { correctIndex } from '../lib/quiz';
import { trackActivity } from '../components/ActivityTracker';
import RequestChangeDialog from '../components/course/RequestChangeDialog';

const API = `${API_BASE}/api`;

// NAVI Avatar URL
const NAVI_AVATAR = "https://customer-assets-7cd3h4nn.emergentagent.net/job_cpd-compass/artifacts/f47qg3qu_image.png";

/**
 * Format NAVI response - removes markdown asterisks and improves readability
 */
const formatNaviResponse = (text) => {
  if (!text) return '';
  
  // Remove all asterisks (bold/italic markdown)
  let formatted = text.replace(/\*+/g, '');
  
  // Clean up any double spaces created
  formatted = formatted.replace(/  +/g, ' ');
  
  // Clean up any leading/trailing whitespace on lines
  formatted = formatted.split('\n').map(line => line.trim()).join('\n');
  
  // Remove excessive newlines (more than 2)
  formatted = formatted.replace(/\n{3,}/g, '\n\n');
  
  return formatted.trim();
};

// Media Library Picker Component for selecting media to embed
function MediaLibraryPicker({ onSelect, filterType = null }) {
  const { token } = useAuth();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  useEffect(() => {
    const fetchMedia = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/media`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMedia(Array.isArray(response.data) ? response.data : (response.data.items || []));
      } catch (error) {
        console.error('Failed to fetch media:', error);
        toast.error('Failed to load media library');
      } finally {
        setLoading(false);
      }
    };
    
    fetchMedia();
  }, [token]);
  
  // Filter media based on type and search term
  const filteredMedia = media.filter(item => {
    // Type filter
    if (filterType) {
      const itemType = item.type || (item.content_type?.startsWith('video/') ? 'video' : 'image');
      if (filterType === 'video' && itemType !== 'video') return false;
      if (filterType === 'image' && itemType !== 'image' && itemType !== 'infographic') return false;
      if (filterType === 'infographic' && itemType !== 'infographic') return false;
    }
    
    // Search filter
    if (searchTerm) {
      return item.filename?.toLowerCase().includes(searchTerm.toLowerCase());
    }
    
    return true;
  });
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#1EBFB3]" />
      </div>
    );
  }
  
  return (
    <div>
      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search media..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-zinc-300 placeholder-zinc-500 focus:border-[#1EBFB3]"
        />
      </div>
      
      {/* Media Grid */}
      {filteredMedia.length > 0 ? (
        <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
          {filteredMedia.map((item) => {
            const isVideo = item.type === 'video' || item.content_type?.startsWith('video/');
            const isDoc = item.type === 'document';
            return (
              <button
                key={item.id}
                onClick={() => onSelect({
                  ...item,
                  type: isVideo ? 'video' : (item.type || 'image'),
                })}
                className="relative aspect-square bg-zinc-800 rounded-lg overflow-hidden border-2 border-transparent hover:border-[#1EBFB3] transition-all group"
              >
                {isDoc ? (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-zinc-800 px-2">
                    <FileText className="h-9 w-9 text-amber-300" />
                    <span className="text-[10px] text-zinc-400 truncate max-w-full">{item.filename}</span>
                  </div>
                ) : isVideo ? (
                  <div className="w-full h-full flex items-center justify-center bg-zinc-800">
                    <Video className="h-10 w-10 text-blue-400" />
                  </div>
                ) : (
                  <img
                    src={`${API_BASE}${item.url}`}
                    alt={item.filename}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/placeholder.png';
                    }}
                  />
                )}
                
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Plus className="h-8 w-8 text-white" />
                </div>
                
                {/* Type Badge */}
                <div className="absolute bottom-1 left-1">
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    isDoc ? 'bg-amber-500/80 text-black' : isVideo ? 'bg-blue-500/80 text-white' : 'bg-[#1EBFB3]/80 text-white'
                  }`}>
                    {isDoc ? 'Document' : isVideo ? 'Video' : 'Image'}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8 text-zinc-400">
          <ImageIcon className="h-10 w-10 mx-auto mb-2 text-zinc-600" />
          <p>No media found</p>
          <p className="text-sm text-zinc-500">Upload media in Admin → Media Library</p>
        </div>
      )}
    </div>
  );
}

// Sortable Section Item Component for drag-and-drop
function SortableSectionItem({ 
  section, 
  idx, 
  currentView, 
  completedSections, 
  editMode, 
  sectionsLength,
  goToSection, 
  handleRemoveSection,
  handleMoveSection
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.section_id || `section-${idx}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={`relative group ${isDragging ? 'bg-zinc-800 rounded-lg' : ''}`}
    >
      <div className="flex items-center">
        {/* Drag Handle - only in edit mode */}
        {editMode && (
          <button
            {...attributes}
            {...listeners}
            className="p-1.5 text-zinc-500 hover:text-zinc-300 cursor-grab active:cursor-grabbing shrink-0"
            title="Drag to reorder"
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}
        
        {/* Section Number Badge */}
        {editMode && (
          <span className="w-6 h-6 rounded-full bg-navigate-gold/20 text-navigate-gold text-xs flex items-center justify-center font-medium shrink-0 mr-1">
            {idx + 1}
          </span>
        )}
        
        <button
          onClick={() => goToSection(idx)}
          className={`flex-1 flex items-start gap-3 p-3 rounded-lg text-left transition-colors ${
            currentView === `section-${idx}`
              ? 'bg-navigate-gold/20 text-navigate-gold' 
              : 'text-zinc-400 hover:bg-zinc-800'
          } ${editMode ? 'pr-20' : ''}`}
        >
          {!editMode && (
            completedSections.has(idx) ? (
              <CheckCircle2 className="h-5 w-5 text-green-400 shrink-0 mt-0.5" />
            ) : (
              <Circle className="h-5 w-5 shrink-0 mt-0.5" />
            )
          )}
          <span className="text-sm leading-snug">{section.title}</span>
        </button>
        
        {/* Move Up/Down and Delete buttons in edit mode */}
        {editMode && (
          <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
            {/* Move Up */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleMoveSection(idx, idx - 1);
              }}
              disabled={idx === 0}
              className="p-1 text-zinc-500 hover:text-zinc-300 disabled:opacity-30 disabled:cursor-not-allowed rounded transition-colors"
              title="Move up"
            >
              <ChevronUp className="h-4 w-4" />
            </button>
            {/* Move Down */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleMoveSection(idx, idx + 1);
              }}
              disabled={idx === sectionsLength - 1}
              className="p-1 text-zinc-500 hover:text-zinc-300 disabled:opacity-30 disabled:cursor-not-allowed rounded transition-colors"
              title="Move down"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
            {/* Delete */}
            {sectionsLength > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveSection(idx);
                }}
                className="p-1 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                title="Delete section"
                data-testid={`delete-section-${idx}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AICoursePlayer() {
  const { courseId: courseIdParam } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, user, isAdmin: isAnyAdmin, isSuperAdmin } = useAuth();
  const isAdmin = isSuperAdmin;
  const canRequestChange = !isSuperAdmin && (isAnyAdmin || user?.role === 'instructor');
  
  // Check if opened in admin edit mode via ?edit=true or ?mode=edit
  const isAdminEditMode = (searchParams.get('edit') === 'true' || searchParams.get('mode') === 'edit') && isAdmin;
  // Optional return destination (e.g. back to the programme builder)
  const returnTo = searchParams.get('returnTo');
  const exitTo = returnTo || '/admin?tab=courses';
  // The default-assessment feature only applies when editing within a programme
  const isProgrammeContext = !!returnTo && returnTo.includes('/admin/programmes/');
  
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [courseMetadata, setCourseMetadata] = useState(null);
  // Store the actual course_id from API response (important when accessed via slug)
  const [actualCourseId, setActualCourseId] = useState(courseIdParam);
  const [currentView, setCurrentView] = useState('overview'); // 'overview', 'section-N', 'quiz', 'results'
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const lessonTitle = course?.sections?.[currentSectionIndex]?.title;
  const viewingLesson = currentView.startsWith('section');
  useEffect(() => { if (lessonTitle && viewingLesson && !isAdminEditMode) trackActivity(token, window.location.pathname, lessonTitle); }, [lessonTitle, viewingLesson, token, isAdminEditMode]);
  const [completedSections, setCompletedSections] = useState(new Set());
  
  // Quiz state
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  
  // NAVI state
  const [naviOpen, setNaviOpen] = useState(false);
  const [naviMessages, setNaviMessages] = useState([
    { role: 'assistant', content: "Hi! I'm NAVI, your AI learning assistant. Ask me anything about this course content!" }
  ]);
  const [naviInput, setNaviInput] = useState('');
  const [naviLoading, setNaviLoading] = useState(false);
  const naviMessagesEndRef = useRef(null);
  
  // NAVI Coach state (for assessments - legacy)
  const [naviCoachOpen, setNaviCoachOpen] = useState(false);
  
  // Enhanced Assessment state
  const [naviAssessmentOpen, setNaviAssessmentOpen] = useState(false);
  
  // Edit Mode state - only enabled when accessed via admin panel with ?edit=true
  const [editMode, setEditMode] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [editedCourse, setEditedCourse] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  // Admin-chosen default assessment mode: '' (learner chooses), 'coach' (NAVI Coach), 'grader' (MCQ)
  const [defaultAssessmentMode, setDefaultAssessmentMode] = useState('');
  const [generationState, setGenerationState] = useState(null);
  const [blockedMessage, setBlockedMessage] = useState('');
  const generationPollRef = useRef(null);
  useEffect(() => () => clearTimeout(generationPollRef.current), []);
  const noQuizRequired = defaultAssessmentMode === 'none';
  const [savingAssessmentMode, setSavingAssessmentMode] = useState(false);
  
  // Initialize edit mode from URL param (only for admins)
  useEffect(() => {
    if (isAdminEditMode) {
      setEditMode(true);
    }
  }, [isAdminEditMode]);
  
  // Assessment Questions state
  const [assessmentQuestions, setAssessmentQuestions] = useState([]);
  const [savingQuestions, setSavingQuestions] = useState(false);
  const [showQuestionsEditor, setShowQuestionsEditor] = useState(false);
  
  // Quiz Editor state (for auto-generated MCQ quiz)
  const [showQuizEditor, setShowQuizEditor] = useState(false);
  const [editedQuiz, setEditedQuiz] = useState([]);
  const [savingQuiz, setSavingQuiz] = useState(false);
  
  // Role Tagging & Duration state
  const [targetRoles, setTargetRoles] = useState([]);
  const [courseDuration, setCourseDuration] = useState({ value: '', unit: 'hours' });
  const [showRoleTagging, setShowRoleTagging] = useState(false);
  
  // Supplementary Materials state
  const [supplementaryMaterials, setSupplementaryMaterials] = useState([]);
  const [showMaterialsPanel, setShowMaterialsPanel] = useState(false);
  
  // AI Generation state
  const [generatingTopics, setGeneratingTopics] = useState(false);
  const [generatingSubsection, setGeneratingSubsection] = useState(null); // index of subsection being generated
  
  // Available roles for tagging
  const AVAILABLE_ROLES = [
    'FAIS Representative / Advisor',
    'Key Individual',
    'Compliance',
    'Operations',
    'Legal / Risk',
    'Audit',
    'IT Operations',
    'Governance',
    'Human Resources',
    'Client Fulfilment and Administration',
    'Other Support Function'
  ];

  useEffect(() => {
    fetchCourse();
  }, [courseIdParam]);

  useEffect(() => {
    naviMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [naviMessages]);
  
  useEffect(() => {
    if (course && !editedCourse) {
      setEditedCourse(JSON.parse(JSON.stringify(course)));
    }
  }, [course]);

  // Auto-save every 60 seconds when in edit mode
  const [lastSavedCourse, setLastSavedCourse] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState(null); // 'saving', 'saved', 'error'
  
  useEffect(() => {
    if (!editMode || !editedCourse) return;
    
    const autoSaveInterval = setInterval(async () => {
      // Check if there are changes to save
      const currentJson = JSON.stringify(editedCourse);
      const savedJson = lastSavedCourse ? JSON.stringify(lastSavedCourse) : JSON.stringify(course);
      
      if (currentJson !== savedJson) {
        setAutoSaveStatus('saving');
        try {
          await axios.put(
            `${API}/ai/course/${actualCourseId}/content`,
            { ai_course_data: editedCourse },
            { headers: { Authorization: `Bearer ${token}` }}
          );
          setCourse(editedCourse);
          setLastSavedCourse(JSON.parse(JSON.stringify(editedCourse)));
          setAutoSaveStatus('saved');
          // Clear status after 3 seconds
          setTimeout(() => setAutoSaveStatus(null), 3000);
        } catch (error) {
          console.error('Auto-save error:', error);
          setAutoSaveStatus('error');
        }
      }
    }, 60000); // 60 seconds
    
    return () => clearInterval(autoSaveInterval);
  }, [editMode, editedCourse, course, lastSavedCourse, actualCourseId, token]);

  const fetchCourse = async () => {
    try {
      // Use courseIdParam for initial fetch (can be course_id or slug)
      const response = await axios.get(`${API}/ai/course/${courseIdParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.data.ai_course_data?.sections?.length && response.data.status === 'generating') {
        setGenerationState({ progress: response.data.generation_progress || 0, message: response.data.generation_message, title: response.data.title });
        setLoading(false);
        generationPollRef.current = setTimeout(fetchCourse, 4000);
        return;
      }
      setGenerationState(null);
      setCourse(response.data.ai_course_data);
      setCourseMetadata(response.data);
      setDefaultAssessmentMode(response.data.default_assessment_mode || '');
      // Store actual course_id from API response for subsequent calls
      setActualCourseId(response.data.course_id);
      setLoading(false);
      
      // Set target roles and duration from course metadata
      if (response.data.target_roles) {
        setTargetRoles(response.data.target_roles);
      }
      if (response.data.course_duration) {
        setCourseDuration(response.data.course_duration);
      }
      
      // Initialize quiz editor with existing quiz
      if (response.data.quiz) {
        setEditedQuiz(response.data.quiz);
      }
      
      // Fetch assessment questions if admin - use actual course_id
      const cid = response.data.course_id;
      if (isAdmin) {
        try {
          const questionsRes = await axios.get(`${API}/ai/course/${cid}/assessment-questions`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAssessmentQuestions(questionsRes.data.questions || []);
        } catch (err) {
          console.log('No assessment questions set');
          setAssessmentQuestions([]);
        }
        
        // Fetch supplementary materials
        try {
          const materialsRes = await axios.get(`${API}/courses/${cid}/materials`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setSupplementaryMaterials(materialsRes.data.materials || []);
        } catch (err) {
          console.log('No supplementary materials');
          setSupplementaryMaterials([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch course:', error);
      if (error.response?.status === 403) setBlockedMessage(error.response.data?.detail || 'This Learning Unit is not available yet.');
      else toast.error('Failed to load course');
      setLoading(false);
    }
  };
  
  // Save edited course content
  const handleSaveCourse = async () => {
    if (!editedCourse) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API}/ai/course/${actualCourseId}/content`,
        { ai_course_data: editedCourse },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setCourse(editedCourse);
      toast.success('Course content saved!');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Save the admin-chosen default assessment mode (persists immediately)
  const handleSaveAssessmentMode = async (mode) => {
    setDefaultAssessmentMode(mode); // optimistic
    setSavingAssessmentMode(true);
    try {
      await axios.put(
        `${API}/admin/courses/${actualCourseId}`,
        { default_assessment_mode: mode },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(
        mode === 'coach' ? 'Default set: NAVI Coach'
          : mode === 'grader' ? 'Default set: MCQ'
          : mode === 'none' ? 'No quiz required — course completes when all sections are viewed'
          : 'Default cleared — learners will choose'
      );
    } catch (error) {
      console.error('Failed to save assessment mode:', error);
      toast.error('Failed to save default assessment mode');
    } finally {
      setSavingAssessmentMode(false);
    }
  };
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  // Get file icon based on type
  const getFileIcon = (fileType) => {
    const icons = {
      pdf: '📄',
      doc: '📝',
      docx: '📝',
      ppt: '📊',
      pptx: '📊',
      xls: '📈',
      xlsx: '📈',
      txt: '📃',
      md: '📃'
    };
    return icons[fileType] || '📎';
  };
  
  // Publish course
  const handlePublishCourse = async () => {
    setPublishing(true);
    try {
      // Save any pending changes first
      if (editedCourse) {
        await axios.put(
          `${API}/ai/course/${actualCourseId}/content`,
          { ai_course_data: editedCourse },
          { headers: { Authorization: `Bearer ${token}` }}
        );
      }
      
      // Publish the course
      await axios.post(
        `${API}/ai/course/${actualCourseId}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success('Course published successfully!', {
        description: 'The course is now available in the course catalog.'
      });
      
      // Refresh metadata
      fetchCourse();
    } catch (error) {
      console.error('Publish error:', error);
      toast.error('Failed to publish course');
    } finally {
      setPublishing(false);
    }
  };
  
  // Handle thumbnail update
  const handleThumbnailUpdate = async (imageUrl) => {
    try {
      // Update the course metadata with new thumbnail
      await axios.put(
        `${API}/admin/courses/${actualCourseId}`,
        { thumbnail_url: imageUrl },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      // Update local state
      setCourseMetadata(prev => ({ ...prev, thumbnail_url: imageUrl }));
      
      toast.success('Course thumbnail updated!', {
        description: 'The thumbnail will appear in course listings and as the header image.'
      });
    } catch (error) {
      console.error('Thumbnail update error:', error);
      toast.error('Failed to update thumbnail');
    }
  };
  
  // Assessment Questions handlers
  const handleAddQuestion = (type = 'open') => {
    if (assessmentQuestions.length >= 10) {
      toast.error('Maximum 10 questions allowed');
      return;
    }
    const newQuestion = {
      type: type, // 'open', 'mcq', 'truefalse'
      question: '',
      options: type === 'mcq' ? ['', '', '', ''] : type === 'truefalse' ? ['True', 'False'] : [],
      correctAnswer: type === 'truefalse' ? 'True' : ''
    };
    setAssessmentQuestions(prev => [...prev, newQuestion]);
  };
  
  const handleRemoveQuestion = (index) => {
    setAssessmentQuestions(prev => prev.filter((_, i) => i !== index));
  };
  
  const handleQuestionChange = (index, field, value) => {
    setAssessmentQuestions(prev => {
      const updated = [...prev];
      if (typeof updated[index] === 'string') {
        // Convert old string format to new object format
        updated[index] = { type: 'open', question: value, options: [], correctAnswer: '' };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return updated;
    });
  };
  
  const handleMCQOptionChange = (qIndex, optionIndex, value) => {
    setAssessmentQuestions(prev => {
      const updated = [...prev];
      const options = [...(updated[qIndex].options || ['', '', '', ''])];
      options[optionIndex] = value;
      updated[qIndex] = { ...updated[qIndex], options };
      return updated;
    });
  };
  
  const handleSaveQuestions = async () => {
    setSavingQuestions(true);
    try {
      // Filter and normalize questions
      const validQuestions = assessmentQuestions
        .filter(q => {
          if (typeof q === 'string') return q.trim();
          return q.question && q.question.trim();
        })
        .map(q => {
          if (typeof q === 'string') {
            return { type: 'open', question: q.trim(), options: [], correctAnswer: '' };
          }
          return {
            type: q.type || 'open',
            question: q.question.trim(),
            options: q.options || [],
            correctAnswer: q.correctAnswer || ''
          };
        });
      
      await axios.put(
        `${API}/ai/course/${actualCourseId}/assessment-questions`,
        { questions: validQuestions },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      setAssessmentQuestions(validQuestions);
      toast.success(`${validQuestions.length} assessment questions saved!`);
    } catch (error) {
      console.error('Save questions error:', error);
      toast.error('Failed to save assessment questions');
    } finally {
      setSavingQuestions(false);
    }
  };

  // AI Assessment Question Generation
  const [generatingAssessment, setGeneratingAssessment] = useState(false);
  
  const handleGenerateAIAssessment = async (numQuestions = 5) => {
    setGeneratingAssessment(true);
    try {
      const response = await axios.post(
        `${API}/ai/course/${actualCourseId}/generate-assessment`,
        { 
          num_questions: numQuestions,
          question_types: ['open', 'mcq', 'truefalse']
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.success && response.data.questions) {
        const newQuestions = response.data.questions;
        
        if (assessmentQuestions.length > 0) {
          const remaining = 10 - assessmentQuestions.length;
          if (remaining <= 0) {
            toast.error('Maximum 10 questions allowed. Remove some to add new ones.');
            return;
          }
          const toAdd = newQuestions.slice(0, remaining);
          setAssessmentQuestions(prev => [...prev, ...toAdd]);
          toast.success(`Added ${toAdd.length} AI-generated questions!`);
        } else {
          setAssessmentQuestions(newQuestions.slice(0, 10));
          toast.success(`Generated ${Math.min(newQuestions.length, 10)} assessment questions!`);
        }
      }
    } catch (error) {
      console.error('AI assessment generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate assessment questions');
    } finally {
      setGeneratingAssessment(false);
    }
  };
  
  // Save quiz (MCQ quiz at end of course)
  const handleSaveQuiz = async () => {
    setSavingQuiz(true);
    try {
      await axios.put(
        `${API}/ai/course/${actualCourseId}/quiz`,
        { quiz: editedQuiz },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Quiz saved successfully!');
    } catch (error) {
      console.error('Save quiz error:', error);
      toast.error('Failed to save quiz');
    } finally {
      setSavingQuiz(false);
    }
  };

  // AI Quiz Generation
  const [generatingQuiz, setGeneratingQuiz] = useState(false);
  const [generatingSectionImages, setGeneratingSectionImages] = useState(false);
  
  const handleGenerateAIQuiz = async (numQuestions = 10) => {
    setGeneratingQuiz(true);
    try {
      const response = await axios.post(
        `${API}/ai/course/${actualCourseId}/generate-quiz`,
        { num_questions: numQuestions },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.success && response.data.questions) {
        // Append to existing questions or replace
        if (editedQuiz.length > 0) {
          // Ask user if they want to replace or append
          const confirmed = window.confirm(
            `You have ${editedQuiz.length} existing questions. Click OK to replace them with ${response.data.questions.length} AI-generated questions, or Cancel to append instead.`
          );
          if (confirmed) {
            setEditedQuiz(response.data.questions);
          } else {
            setEditedQuiz(prev => [...prev, ...response.data.questions]);
          }
        } else {
          setEditedQuiz(response.data.questions);
        }
        toast.success(`Generated ${response.data.questions.length} quiz questions!`);
      }
    } catch (error) {
      console.error('AI quiz generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate quiz questions');
    } finally {
      setGeneratingQuiz(false);
    }
  };
  
  // Quiz editing functions
  const addQuizQuestion = () => {
    const newQuestion = {
      id: `q_${Date.now()}`,
      question: '',
      options: ['', '', '', ''],
      correctAnswer: 0
    };
    setEditedQuiz(prev => [...prev, newQuestion]);
  };
  
  const updateQuizQuestion = (index, field, value) => {
    setEditedQuiz(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };
  
  const updateQuizOption = (qIndex, optIndex, value) => {
    setEditedQuiz(prev => {
      const updated = [...prev];
      const options = [...updated[qIndex].options];
      options[optIndex] = value;
      updated[qIndex] = { ...updated[qIndex], options };
      return updated;
    });
  };
  
  const deleteQuizQuestion = (index) => {
    setEditedQuiz(prev => prev.filter((_, i) => i !== index));
  };
  
  // Toggle role in target roles
  const handleToggleRole = (role) => {
    setTargetRoles(prev => {
      if (prev.includes(role)) {
        return prev.filter(r => r !== role);
      }
      return [...prev, role];
    });
  };
  
  // Save target roles and duration
  const handleSaveRoleTagging = async () => {
    try {
      await axios.put(
        `${API}/ai/course/${actualCourseId}/role-tagging`,
        { 
          target_roles: targetRoles,
          course_duration: courseDuration
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success('Role tagging and duration saved!');
    } catch (error) {
      console.error('Save role tagging error:', error);
      toast.error('Failed to save role tagging');
    }
  };

  // Generate AI images for all sections
  const handleGenerateSectionImages = async () => {
    if (!actualCourseId || !editedCourse?.sections) return;
    
    setGeneratingSectionImages(true);
    const sectionsToGenerate = editedCourse.sections
      .map((s, i) => ({ ...s, index: i }))
      .filter(s => !s.image_url);
    
    if (sectionsToGenerate.length === 0) {
      toast.info('All sections already have images');
      setGeneratingSectionImages(false);
      return;
    }
    
    toast.info(`Generating ${sectionsToGenerate.length} section images...`);
    
    let successCount = 0;
    let failCount = 0;
    
    try {
      // Generate images for each section without an image
      for (const section of sectionsToGenerate) {
        const i = section.index;
        try {
          const response = await axios.post(
            `${API}/ai/course/${actualCourseId}/section/${i}/generate-image`,
            { section_title: section.title },
            { headers: { Authorization: `Bearer ${token}` }}
          );
          
          if (response.data.image_url) {
            successCount++;
            // Update local state with the new image
            setEditedCourse(prev => {
              const updated = { ...prev };
              updated.sections = [...updated.sections];
              updated.sections[i] = { ...updated.sections[i], image_url: response.data.image_url };
              return updated;
            });
            
            // Also update the original course
            setCourse(prev => {
              if (!prev?.ai_course_data?.sections) return prev;
              const updated = { ...prev };
              updated.ai_course_data = { ...updated.ai_course_data };
              updated.ai_course_data.sections = [...updated.ai_course_data.sections];
              updated.ai_course_data.sections[i] = { 
                ...updated.ai_course_data.sections[i], 
                image_url: response.data.image_url 
              };
              return updated;
            });
          } else {
            failCount++;
          }
        } catch (err) {
          failCount++;
          console.error(`Failed to generate image for section ${i}:`, err);
        }
      }
      
      // Show appropriate toast based on results
      if (successCount > 0 && failCount === 0) {
        toast.success(`Generated ${successCount} section images!`);
      } else if (successCount > 0 && failCount > 0) {
        toast.warning(`Generated ${successCount} images. ${failCount} failed.`);
      } else {
        toast.error('Failed to generate section images');
      }
    } catch (error) {
      console.error('Generate section images error:', error);
      toast.error('Failed to generate section images');
    } finally {
      setGeneratingSectionImages(false);
    }
  };
  
  // Add new section
  const handleAddSection = () => {
    if (!editedCourse) return;
    
    const sectionIndex = (editedCourse.sections || []).length;
    const newSection = {
      section_id: `section_new_${Date.now()}`,
      title: 'New Section',
      content: '## Introduction\n\nAdd your content here. You can use the AI wand button to generate content.\n\n### Key Points\n\n- Point 1\n- Point 2\n- Point 3',
      key_takeaways: ['Add key takeaway 1', 'Add key takeaway 2'],
      practical_examples: ['Add a practical example here'],
      subsections: [
        {
          title: 'Introduction',
          content: 'Add your content here. You can use the AI wand button to generate content.'
        }
      ],
      image_url: null,
      image_focal_point: 'center'
    };
    
    setEditedCourse(prev => ({
      ...prev,
      sections: [...(prev.sections || []), newSection]
    }));
    
    // Navigate to the new section
    setCurrentSectionIndex(sectionIndex);
    setCurrentView(`section-${sectionIndex}`);
    
    toast.success('New section added! Edit the title and content below.');
  };
  
  // Remove section
  const handleRemoveSection = (index) => {
    if (!editedCourse || editedCourse.sections.length <= 1) {
      toast.error('Course must have at least one section');
      return;
    }
    
    setEditedCourse(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index)
    }));
    
    // Reset current section if needed
    if (currentSectionIndex >= editedCourse.sections.length - 1) {
      setCurrentSectionIndex(Math.max(0, currentSectionIndex - 1));
    }
    
    toast.success('Section removed');
  };

  // Move section up or down
  const handleMoveSection = (fromIndex, toIndex) => {
    if (!editedCourse || toIndex < 0 || toIndex >= editedCourse.sections.length) {
      return;
    }
    
    setEditedCourse(prev => {
      const newSections = [...prev.sections];
      const [movedSection] = newSections.splice(fromIndex, 1);
      newSections.splice(toIndex, 0, movedSection);
      return { ...prev, sections: newSections };
    });
    
    // Also update the course state so it syncs with editedCourse
    setCourse(prev => {
      const newSections = [...prev.sections];
      const [movedSection] = newSections.splice(fromIndex, 1);
      newSections.splice(toIndex, 0, movedSection);
      return { ...prev, sections: newSections };
    });
    
    toast.success(`Section moved to position ${toIndex + 1}`);
  };

  // Handle drag end for reordering sections
  const handleSectionDragEnd = (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const sections = editedCourse?.sections || [];
    const oldIndex = sections.findIndex(s => (s.section_id || `section-${sections.indexOf(s)}`) === active.id);
    const newIndex = sections.findIndex(s => (s.section_id || `section-${sections.indexOf(s)}`) === over.id);
    
    if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
      setEditedCourse(prev => ({
        ...prev,
        sections: arrayMove(prev.sections, oldIndex, newIndex)
      }));
      
      setCourse(prev => ({
        ...prev,
        sections: arrayMove(prev.sections, oldIndex, newIndex)
      }));
      
      toast.success('Sections reordered');
    }
  };

  // DnD sensors for sections
  const sectionSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // AI Generate new topics/sections
  const handleAIGenerateTopics = async () => {
    if (!editedCourse) return;
    
    setGeneratingTopics(true);
    try {
      const existingTitles = (editedCourse.sections || []).map(s => s.title);
      const response = await axios.post(
        `${API}/ai/course/${actualCourseId}/generate-topics`,
        {
          num_topics: 2,
          target_audience: courseMetadata?.target_job_title || 'Professionals'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success && response.data.topics) {
        // Convert AI topics to course sections format
        const newSections = response.data.topics.map((topic, idx) => ({
          title: topic.title,
          content: topic.content || '',
          key_takeaways: topic.key_takeaways || [],
          practical_examples: topic.subsections?.map(s => s.title) || [],
          section_image: null,
          subsections: topic.subsections || []
        }));
        
        setEditedCourse(prev => ({
          ...prev,
          sections: [...(prev.sections || []), ...newSections]
        }));
        
        toast.success(`${newSections.length} new topics generated by AI!`);
      }
    } catch (error) {
      console.error('AI topic generation error:', error);
      toast.error('Failed to generate topics');
    } finally {
      setGeneratingTopics(false);
    }
  };
  
  // AI Generate content for a subsection
  const handleAIGenerateSubsection = async (subsectionIndex, subsectionTitle, updateCallback) => {
    if (!subsectionTitle || subsectionTitle.trim() === '') {
      toast.error('Please enter a subsection title first');
      return;
    }
    
    setGeneratingSubsection(subsectionIndex);
    try {
      const currentSection = (editedCourse?.sections || course?.sections)?.[currentSectionIndex];
      
      const response = await axios.post(
        `${API}/ai/course/${actualCourseId}/generate-subsection`,
        {
          section_title: currentSection?.title || 'Course Section',
          subsection_title: subsectionTitle,
          target_audience: courseMetadata?.target_job_title || 'Professionals'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success && response.data.content) {
        // Call the update callback with the generated content
        const generatedContent = response.data.content.content || response.data.content;
        if (updateCallback) {
          updateCallback(generatedContent);
        }
        toast.success('Content generated by AI!');
      }
    } catch (error) {
      console.error('AI subsection generation error:', error);
      toast.error('Failed to generate content');
    } finally {
      setGeneratingSubsection(null);
    }
  };

  
  // Update section content
  const updateSectionContent = (sectionIndex, field, value) => {
    // Visuals are saved server-side immediately, so mirror them into the read-only course state too
    if (field === 'visual_blocks' || field === 'visual_review') {
      setCourse((prev) => prev?.sections?.[sectionIndex]
        ? { ...prev, sections: prev.sections.map((sec, i) => (i === sectionIndex ? { ...sec, [field]: value } : sec)) } : prev);
    }
    setEditedCourse(prev => {
      const updated = { ...prev };
      if (updated.sections && updated.sections[sectionIndex]) {
        updated.sections[sectionIndex] = {
          ...updated.sections[sectionIndex],
          [field]: value
        };
      }
      return updated;
    });
  };
  
  // Update overview content
  const updateOverview = (field, value) => {
    setEditedCourse(prev => {
      const updated = { ...prev };
      updated.overview[field] = value;
      return updated;
    });
  };

  const handleNaviChat = async () => {
    if (!naviInput.trim() || naviLoading) return;
    
    const userMessage = naviInput.trim();
    setNaviInput('');
    setNaviMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setNaviLoading(true);
    
    try {
      const currentSection = course?.sections?.[currentSectionIndex]?.title || null;
      
      const response = await axios.post(`${API}/ai/navi/chat`, {
        message: userMessage,
        course_id: actualCourseId,
        current_section: currentSection,
        chat_history: naviMessages.slice(-6)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNaviMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response 
      }]);
    } catch (error) {
      console.error('NAVI chat failed:', error);
      setNaviMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm having trouble connecting right now. Please try again in a moment." 
      }]);
    } finally {
      setNaviLoading(false);
    }
  };

  const goToSection = (index) => {
    setCurrentSectionIndex(index);
    setCurrentView(`section-${index}`);
    // Scroll to top when navigating to a new section
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const markSectionComplete = () => {
    setCompletedSections(prev => new Set([...prev, currentSectionIndex]));
  };

  const nextSection = () => {
    markSectionComplete();
    if (currentSectionIndex < (course?.sections?.length || 0) - 1) {
      goToSection(currentSectionIndex + 1);
    } else if (noQuizRequired) {
      completeWithoutQuiz();
    } else {
      // Last section completed - open Enhanced Assessment
      setNaviAssessmentOpen(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const completeWithoutQuiz = async () => {
    if (previewMode || editMode) {
      setCurrentView('no-quiz-complete');
      return;
    }
    try {
      await axios.post(`${API}/courses/${actualCourseId}/complete`, { passed: true }, { headers: { Authorization: `Bearer ${token}` } });
      setCurrentView('no-quiz-complete');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.success('Course completed!');
    } catch (e) {
      toast.error('Could not mark the course complete — please try again');
    }
  };

  const prevSection = () => {
    if (currentSectionIndex > 0) {
      goToSection(currentSectionIndex - 1);
    } else {
      setCurrentView('overview');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleQuizAnswer = (questionId, answer) => {
    setQuizAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const submitQuiz = () => {
    const quiz = course?.quiz || [];
    let correct = 0;
    
    quiz.forEach(q => {
      const questionId = q.id || q.question_id;
      const userAnswer = quizAnswers[questionId];
      const correctAnswer = correctIndex(q);
      if (userAnswer === correctAnswer) {
        correct++;
      }
    });
    
    setQuizScore(correct);
    setQuizSubmitted(true);
    setCurrentView('results');
    const answers = Object.fromEntries(Object.entries(quizAnswers).map(([k, v]) => [String(k), v]));
    axios.post(`${API}/rubric/course-quiz/${actualCourseId}/submit`, { answers }, { headers: { Authorization: `Bearer ${token}` } })
      .catch((e) => console.warn('Quiz result not recorded', e));
  };

  const progress = course?.sections?.length 
    ? Math.round((completedSections.size / course.sections.length) * 100) 
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-navigate-dark flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#1EBFB3] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white">Loading course...</p>
        </div>
      </div>
    );
  }

  if (!course && generationState) return <CourseGeneratingScreen {...generationState} />;
  if (!course && blockedMessage) {
    return (
      <div className="min-h-screen bg-navigate-dark flex items-center justify-center px-6" data-testid="course-under-review-screen">
        <div className="max-w-md text-center">
          <p className="text-white text-xl font-semibold mb-2">{/enrolled/i.test(blockedMessage) ? 'Not enrolled' : 'Almost ready'}</p>
          <p className="text-zinc-400 mb-6">{blockedMessage}</p>
          <button onClick={() => navigate(-1)} className="text-sm text-teal-300 hover:text-teal-200" data-testid="course-under-review-back">Go back</button>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-navigate-dark flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">Course not found</p>
          <Link to="/dashboard" className="text-navigate-gold hover:underline">
            Return to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navigate-dark flex">
      {/* Sidebar */}
      <div className="w-80 bg-navigate-surface border-r border-zinc-800 flex flex-col shrink-0">
        {/* Course Header */}
        <div className="p-6 border-b border-zinc-800">
          <Link 
            to="/dashboard" 
            className="flex items-center gap-2 text-zinc-400 hover:text-white mb-4 text-sm"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Dashboard
          </Link>
          <h2 className="font-heading text-lg font-bold text-white line-clamp-2">
            {course.overview?.title}
          </h2>
          <div className="flex items-center gap-2 mt-2 text-sm text-zinc-400">
            <Clock className="h-4 w-4" />
            {course.overview?.estimated_duration}
          </div>
        </div>

        {/* Progress */}
        <div className="p-4 border-b border-zinc-800">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-zinc-400">Progress</span>
            <span className="text-navigate-gold font-medium">{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Overview */}
          <button
            onClick={() => setCurrentView('overview')}
            className={`w-full flex items-start gap-3 p-3 rounded-lg mb-2 text-left transition-colors ${
              currentView === 'overview' 
                ? 'bg-navigate-gold/20 text-navigate-gold' 
                : 'text-zinc-400 hover:bg-zinc-800'
            }`}
          >
            <Home className="h-5 w-5 shrink-0 mt-0.5" />
            <span>Course Overview</span>
          </button>

          {/* Sections - with drag and drop in edit mode */}
          <div className="space-y-1">
            {editMode ? (
              <DndContext
                sensors={sectionSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleSectionDragEnd}
              >
                <SortableContext
                  items={(editedCourse?.sections || []).map((s, i) => s.section_id || `section-${i}`)}
                  strategy={verticalListSortingStrategy}
                >
                  {(editedCourse?.sections || []).map((section, idx) => (
                    <SortableSectionItem
                      key={section.section_id || `section-${idx}`}
                      section={section}
                      idx={idx}
                      currentView={currentView}
                      completedSections={completedSections}
                      editMode={editMode}
                      sectionsLength={editedCourse?.sections?.length || 0}
                      goToSection={goToSection}
                      handleRemoveSection={handleRemoveSection}
                      handleMoveSection={handleMoveSection}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            ) : (
              // Non-edit mode - simple list without drag
              (course.sections || []).map((section, idx) => (
                <div key={section.section_id || idx} className="relative group">
                  <button
                    onClick={() => goToSection(idx)}
                    className={`w-full flex items-start gap-3 p-3 rounded-lg text-left transition-colors ${
                      currentView === `section-${idx}`
                        ? 'bg-navigate-gold/20 text-navigate-gold' 
                        : 'text-zinc-400 hover:bg-zinc-800'
                    }`}
                  >
                    {completedSections.has(idx) ? (
                      <CheckCircle2 className="h-5 w-5 text-green-400 shrink-0 mt-0.5" />
                    ) : (
                      <Circle className="h-5 w-5 shrink-0 mt-0.5" />
                    )}
                    <span className="text-sm leading-snug">{section.title}</span>
                  </button>
                </div>
              ))
            )}
          </div>

          {/* NAVI Coaching - Course Completion */}
          <div className="mt-4 pt-4 border-t border-zinc-700">
            <p className="text-xs text-zinc-500 mb-2 px-1">Complete the course</p>
            {/* Lock assessment until all sections are completed */}
            {(() => {
              const allSectionsComplete = course?.sections?.every(s => s.user_progress?.completed) || false;
              const sectionCount = course?.sections?.length || 0;
              const completedCount = course?.sections?.filter(s => s.user_progress?.completed).length || 0;
              
              return (
                <button
                  onClick={() => {
                    if (!allSectionsComplete && sectionCount > 0) {
                      toast.error('Complete all lessons first', {
                        description: `You have completed ${completedCount} of ${sectionCount} lessons. Complete all lessons before ${noQuizRequired ? 'finishing the course' : 'attempting the assessment'}.`
                      });
                      return;
                    }
                    if (noQuizRequired) { completeWithoutQuiz(); return; }
                    setNaviAssessmentOpen(true);
                  }}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors border ${
                    allSectionsComplete || sectionCount === 0
                      ? 'bg-gradient-to-r from-[#1EBFB3]/20 to-navigate-gold/20 hover:from-[#1EBFB3]/30 hover:to-navigate-gold/30 border-[#1EBFB3]/30'
                      : 'bg-zinc-800/50 border-zinc-700 opacity-60 cursor-not-allowed'
                  }`}
                  data-testid="navi-assessment-btn"
                >
                  <img 
                    src="https://customer-assets-7cd3h4nn.emergentagent.net/job_cpd-compass/artifacts/f47qg3qu_image.png" 
                    alt="NAVI" 
                    className={`w-6 h-6 rounded-full object-cover shrink-0 ${
                      allSectionsComplete || sectionCount === 0 ? '' : 'opacity-50 grayscale'
                    }`}
                  />
                  <div className="flex-1">
                    <span className={`font-medium flex items-center gap-1 ${
                      allSectionsComplete || sectionCount === 0 ? 'text-[#1EBFB3]' : 'text-zinc-400'
                    }`}>
 {noQuizRequired ? 'Finish course' : 'Assessment'}
                      <Sparkles className={`h-3 w-3 ${allSectionsComplete || sectionCount === 0 ? 'text-navigate-gold' : 'text-zinc-500'}`} />
                    </span>
                    <span className="text-xs text-zinc-500 block">
                      {allSectionsComplete || sectionCount === 0 
                        ? (noQuizRequired ? 'No quiz required' : 'Complete to earn certificate') 
                        : `Complete all ${sectionCount} lessons first`}
                    </span>
                  </div>
                  {!allSectionsComplete && sectionCount > 0 && (
                    <div className="w-2 h-2 bg-amber-500 rounded-full" />
                  )}
                </button>
              );
            })()}
          </div>
          
          {canRequestChange && (
            <div className="mt-4 pt-4 border-t border-zinc-700">
              <RequestChangeDialog courseId={actualCourseId} courseTitle={course?.title} sections={course?.sections || []} currentIndex={currentView.startsWith('section') ? currentSectionIndex : null} token={token} />
            </div>
          )}

          {/* Admin: Edit & Publish Controls - Only visible for admins */}
          {isAdmin && (
            <div className="mt-4 pt-4 border-t border-zinc-700 space-y-2">
              <p className="text-xs text-zinc-500 mb-2 px-1">Admin Controls</p>
              
              {/* Preview as Student Toggle */}
              <button
                onClick={() => {
                  setPreviewMode(true);
                  setEditMode(false);
                }}
                className="w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors border border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-white"
                data-testid="preview-student-btn"
              >
                <Eye className="h-5 w-5 shrink-0" />
                <span className="truncate">Preview as Student</span>
              </button>
              
              {/* Edit Mode Toggle */}
              <button
                onClick={() => setEditMode(!editMode)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors border ${
                  editMode 
                    ? 'bg-[#1EBFB3]/20 text-[#1EBFB3] border-[#1EBFB3]' 
                    : 'text-zinc-400 hover:bg-zinc-800 border-zinc-700'
                }`}
                data-testid="edit-mode-toggle"
              >
                <PenLine className="h-5 w-5 shrink-0" />
                <span className="truncate flex items-center gap-2">
                  {editMode ? 'Exit Edit Mode' : 'Edit Content'}
                  {editMode && <Edit3 className="h-3 w-3" />}
                </span>
              </button>
              
              {editMode && (
                <>
                  <p className="text-xs text-[#1EBFB3] px-3">
                    Click on content to edit. Changes are saved locally until you click Save.
                  </p>

                  <EnhanceAllVisualsButton courseId={actualCourseId} token={token} onSaved={(saved) => {
                    const apply = (c) => c && c.sections ? { ...c, sections: c.sections.map((sec, i) => (saved[i] ? { ...sec, visual_blocks: saved[i], visual_review: { status: 'pending' } } : sec)) } : c;
                    setEditedCourse(apply);
                    setCourse(apply);
                  }} />

                  <GenerateAllImagesButton courseId={actualCourseId} token={token} sections={editedCourse?.sections || course?.sections || []} onImage={(idx, url) => {
                    const apply = (c) => c && c.sections ? { ...c, sections: c.sections.map((sec, i) => (i === idx ? { ...sec, image_url: url } : sec)) } : c;
                    setEditedCourse(apply);
                    setCourse(apply);
                  }} />
                  
                  {/* Save Button */}
                  <button
                    onClick={handleSaveCourse}
                    disabled={saving}
                    className="w-full flex items-center justify-center gap-2 p-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors disabled:opacity-50"
                    data-testid="save-course-btn"
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                  
                  {/* Add New Section Button */}
                  <button
                    onClick={handleAddSection}
                    className="w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors border border-dashed border-zinc-600 text-zinc-400 hover:bg-zinc-800 hover:border-zinc-500"
                    data-testid="add-section-btn"
                  >
                    <Plus className="h-5 w-5 shrink-0" />
                    <span className="truncate">Add New Section</span>
                  </button>
                  
                  {/* Manage Quiz Button */}
                  <button
                    onClick={() => setShowQuizEditor(true)}
                    className="w-full flex items-center justify-between p-3 rounded-lg transition-colors bg-purple-600/20 border border-purple-500/30 text-white hover:bg-purple-600/30"
                    data-testid="manage-quiz-btn"
                  >
                    <div className="flex items-center gap-2">
                      <HelpCircle className="h-4 w-4 text-purple-400" />
                      <span className="text-sm font-medium">Manage Quiz</span>
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-400">
                      {(editedQuiz || []).length} questions
                    </span>
                  </button>

                  {/* Default Assessment Mode selector — only when editing within a programme */}
                  {isProgrammeContext && (
                  <div className="p-3 rounded-lg bg-zinc-800/60 border border-zinc-700" data-testid="default-assessment-mode">
                    <div className="flex items-center gap-2 mb-1">
                      <Brain className="h-4 w-4 text-[#1EBFB3]" />
                      <span className="text-sm font-medium text-white">Default Assessment</span>
                      {savingAssessmentMode && <Loader2 className="h-3 w-3 animate-spin text-zinc-400" />}
                    </div>
                    <p className="text-xs text-zinc-500 mb-2">
                      Choose what learners get by default. If set to &quot;Let learner choose&quot;, they pick MCQ or NAVI Coach themselves. &quot;No quiz required&quot; completes the course once all sections are viewed.
                    </p>
                    <select
                      value={defaultAssessmentMode}
                      onChange={(e) => handleSaveAssessmentMode(e.target.value)}
                      className="w-full bg-zinc-900 border border-zinc-700 rounded-md text-sm text-white px-2 py-2 focus:outline-none focus:border-[#1EBFB3]"
                      data-testid="default-assessment-mode-select"
                    >
                      <option value="">Let learner choose</option>
                      <option value="coach">NAVI Coach (Interactive)</option>
                      <option value="grader">MCQ (Multiple Choice)</option>
                      <option value="none">No quiz required</option>
                    </select>
                  </div>
                  )}
                </>
              )}

              {/* Publish Status & Button (hidden in programmes: learners see it once the activity/module is published) */}
              {isProgrammeContext ? (
                <div className="p-3 rounded-lg border border-teal-500/30 bg-teal-500/10 text-xs text-teal-200" data-testid="programme-publish-hint">
                  Learners see this course once its activity or module is published in the Programme Builder.
                </div>
              ) : (
              <div className={`p-3 rounded-lg border ${
                courseMetadata?.is_published 
                  ? 'bg-green-500/10 border-green-500/30' 
                  : 'bg-amber-500/10 border-amber-500/30'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {courseMetadata?.is_published ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 text-green-400" />
                      <span className="text-green-400 text-sm font-medium">Published</span>
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 text-amber-400" />
                      <span className="text-amber-400 text-sm font-medium">Draft</span>
                    </>
                  )}
                </div>
                
                {!courseMetadata?.is_published && (
                  <button
                    onClick={handlePublishCourse}
                    disabled={publishing}
                    className="w-full flex items-center justify-center gap-2 p-2 rounded-lg bg-navigate-gold hover:bg-navigate-gold/90 text-black font-medium transition-colors disabled:opacity-50"
                    data-testid="publish-course-btn"
                  >
                    {publishing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Rocket className="h-4 w-4" />
                    )}
                    {publishing ? 'Publishing...' : 'Publish Course'}
                  </button>
                )}
                
                {courseMetadata?.is_published && (
                  <Link 
                    to="/courses" 
                    className="text-xs text-green-400 hover:underline"
                  >
                    View in catalog →
                  </Link>
                )}
              </div>
              )}
              
              {/* Back to Admin / Programme Button */}
              <button
                onClick={() => navigate(exitTo)}
                className="w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors border border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-white mt-2"
                data-testid="back-to-admin-btn"
              >
                <ChevronLeft className="h-5 w-5 shrink-0" />
                <span className="truncate">{returnTo ? 'Back to Programme Builder' : 'Back to Admin'}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Preview Mode Banner */}
        {previewMode && isAdmin && (
          <div className="sticky top-0 z-10 bg-blue-600 text-white p-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Eye className="h-5 w-5" />
              <span className="font-medium">Preview Mode</span>
              <span className="text-blue-200 text-sm">&mdash; You&apos;re seeing this course as students will see it</span>
            </div>
            <button
              onClick={() => {
                setPreviewMode(false);
                setEditMode(true);
              }}
              className="flex items-center gap-2 px-4 py-1.5 bg-white text-blue-600 rounded-lg font-medium hover:bg-blue-50 transition-colors"
            >
              <X className="h-4 w-4" />
              Exit Preview
            </button>
          </div>
        )}
        
        {/* Edit Mode Banner */}
        {editMode && isAdmin && (
          <div className="sticky top-0 z-10 bg-[#1EBFB3] text-white p-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <PenLine className="h-5 w-5" />
              <span className="font-medium">Edit Mode</span>
              <span className="text-white/80 text-sm">&mdash; Click on any content to edit it</span>
              {/* Auto-save status indicator */}
              {autoSaveStatus && (
                <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                  autoSaveStatus === 'saving' ? 'bg-white/20 text-white' :
                  autoSaveStatus === 'saved' ? 'bg-green-500/50 text-green-100' :
                  'bg-red-500/50 text-red-100'
                }`}>
                  {autoSaveStatus === 'saving' && '⏳ Auto-saving...'}
                  {autoSaveStatus === 'saved' && '✓ Auto-saved'}
                  {autoSaveStatus === 'error' && '⚠ Auto-save failed'}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSaveCourse}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-1.5 bg-white text-[#1EBFB3] rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => { if (returnTo) { navigate(returnTo); } else { setEditMode(false); } }}
                className="flex items-center gap-2 px-4 py-1.5 bg-white/20 text-white rounded-lg font-medium hover:bg-white/30 transition-colors"
              >
                <X className="h-4 w-4" />
                {returnTo ? 'Done — Back to Programme' : 'Exit Edit'}
              </button>
            </div>
          </div>
        )}
        
        {/* Course Image Upload - Shows at top when in Edit Mode on any section */}
        {editMode && isAdmin && currentView.startsWith('section-') && (
          <div className="p-6 border-b border-zinc-800 bg-zinc-900/50">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <ImageIcon className="h-5 w-5 text-navigate-gold" />
                    Course Image
                  </h3>
                  <p className="text-zinc-400 text-sm">Course cover. Sections without their own header image use it as a fallback</p>
                </div>
              </div>
              <CourseImageManager
                currentImageUrl={courseMetadata?.thumbnail_url}
                onImageSelect={handleThumbnailUpdate}
                courseTitle={course?.overview?.title || ''}
                courseDescription={course?.overview?.description || ''}
                token={token}
              />
            </div>
          </div>
        )}

        {currentView === 'overview' && (
          <OverviewSection 
            overview={editMode ? editedCourse?.overview : course.overview} 
            onStart={() => goToSection(0)}
            editMode={editMode}
            onUpdateOverview={updateOverview}
            previewMode={previewMode}
            materials={supplementaryMaterials}
            thumbnailUrl={courseMetadata?.thumbnail_url}
          />
        )}

        {editMode && (
          <div className="max-w-4xl mx-auto px-8 pt-6">
            <CourseReviewBanner courseId={actualCourseId} token={token} review={courseMetadata?.content_review}
              sections={editedCourse?.sections || course?.sections || []} quiz={course?.quiz || []} quizFlags={course?.quiz_flags || []}
              onChange={(r) => setCourseMetadata((m) => ({ ...m, content_review: r }))} />
          </div>
        )}
        {currentView.startsWith('section-') && (editMode ? editedCourse?.sections?.[currentSectionIndex] : course.sections?.[currentSectionIndex]) && (
          <ContentSection
            section={editMode ? editedCourse?.sections?.[currentSectionIndex] : course.sections[currentSectionIndex]}
            sectionNumber={currentSectionIndex + 1}
            totalSections={(editMode ? editedCourse?.sections : course.sections)?.length || 0}
            onNext={nextSection}
            onPrev={prevSection}
            isFirst={currentSectionIndex === 0}
            isLast={currentSectionIndex === ((editMode ? editedCourse?.sections : course.sections)?.length || 1) - 1}
            noQuiz={noQuizRequired}
            courseId={actualCourseId}
            token={token}
            editMode={editMode}
            courseThumbnail={courseMetadata?.thumbnail_url}
            onUpdateContent={(field, value) => updateSectionContent(currentSectionIndex, field, value)}
            onImageUpload={(sectionIdx, imageUrl) => {
              // Update both course and editedCourse states with new image
              // This ensures the image persists whether in edit mode or not
              setCourse(prev => {
                const newCourse = { ...prev };
                if (newCourse.sections?.[sectionIdx]) {
                  newCourse.sections[sectionIdx].image_url = imageUrl;
                }
                return newCourse;
              });
              // Also update editedCourse for edit mode (use functional form to always update)
              setEditedCourse(prev => {
                if (!prev) return prev;
                const newCourse = { ...prev };
                if (newCourse.sections?.[sectionIdx]) {
                  newCourse.sections[sectionIdx].image_url = imageUrl;
                }
                return newCourse;
              });
            }}
            onAIGenerateSubsection={handleAIGenerateSubsection}
            generatingSubsection={generatingSubsection}
            courseMetadata={courseMetadata}
            onAIGenerateSectionImage={async (sectionIdx) => {
              // Generate image for specific section
              try {
                const section = editedCourse?.sections?.[sectionIdx] || course.sections?.[sectionIdx];
                const response = await axios.post(
                  `${API}/ai/course/${actualCourseId}/section/${sectionIdx}/generate-image`,
                  { section_title: section?.title || 'Section' },
                  { headers: { Authorization: `Bearer ${token}` }}
                );
                
                if (response.data.image_url) {
                  // Update both states
                  setCourse(prev => {
                    const newCourse = { ...prev };
                    if (newCourse.sections?.[sectionIdx]) {
                      newCourse.sections[sectionIdx].image_url = response.data.image_url;
                    }
                    return newCourse;
                  });
                  setEditedCourse(prev => {
                    if (!prev) return prev;
                    const newCourse = { ...prev };
                    if (newCourse.sections?.[sectionIdx]) {
                      newCourse.sections[sectionIdx].image_url = response.data.image_url;
                    }
                    return newCourse;
                  });
                  toast.success('Section image generated!');
                }
              } catch (error) {
                console.error('Failed to generate section image:', error);
                toast.error('Failed to generate image');
              }
            }}
          />
        )}

        {currentView === 'no-quiz-complete' && (
          <div className="p-8" data-testid="no-quiz-complete-view">
            <div className="max-w-xl mx-auto text-center">
              <div className="w-20 h-20 bg-green-500/15 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="h-10 w-10 text-green-400" />
              </div>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">Course complete</h2>
              <p className="text-zinc-400 mb-6">You&apos;ve worked through every section.</p>
              {searchParams.get('programme') ? (
                <NextActivityCard programmeId={searchParams.get('programme')} activityId={searchParams.get('activity')} token={token} />
              ) : (
                <button onClick={() => navigate(-1)} className="px-6 py-3 bg-navigate-gold text-black rounded-lg font-medium hover:bg-navigate-gold-light transition-colors" data-testid="no-quiz-back-btn">
                  Back
                </button>
              )}
            </div>
          </div>
        )}

        {/* Course Completion - Assessment Options */}
        {currentView === 'complete' && (
          <div className="p-8">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-navigate-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Award className="h-10 w-10 text-navigate-gold" />
                </div>
                <h2 className="text-2xl font-heading font-bold text-white mb-3">
                  All Sections Complete!
                </h2>
                <p className="text-zinc-400">
                  Choose how you&apos;d like to demonstrate your understanding and earn your certificate.
                </p>
              </div>
              
              {/* Assessment Options Grid */}
              <div className="grid gap-4">
                {/* NAVI Coach - Intermediate */}
                <button
                  onClick={() => setNaviAssessmentOpen(true)}
                  className="p-5 bg-navigate-surface border border-zinc-700 rounded-xl hover:border-[#1EBFB3]/50 transition-all text-left group"
                  data-testid="start-navi-coach"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#1EBFB3]/20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#1EBFB3]/30 transition-colors">
                      <Brain className="h-6 w-6 text-[#1EBFB3]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                        Assessment
                        <span className="text-xs px-2 py-0.5 bg-[#1EBFB3]/20 text-[#1EBFB3] rounded-full">Interactive</span>
                        <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">Intermediate Certificate</span>
                      </h3>
                      <p className="text-sm text-zinc-400 mt-1">
                        Engage in guided conversation with NAVI. Choose between Coach Me (interactive) or Grader (MCQ) modes.
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                        <span>10-15 minutes</span>
                        <span>•</span>
                        <span>Personalized feedback</span>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-zinc-500 group-hover:text-[#1EBFB3] transition-colors" />
                  </div>
                </button>
                
                {/* Simulated Lab - Practical Application */}
                <button
                  onClick={() => navigate(`/labs?topic=${encodeURIComponent(course?.specialisations?.[0] || course?.category || '')}`)}
                  className="p-5 bg-navigate-surface border border-zinc-700 rounded-xl hover:border-amber-500/50 transition-all text-left group"
                  data-testid="start-simulated-lab"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-amber-500/30 transition-colors">
                      <Beaker className="h-6 w-6 text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                        Simulated Lab
                        <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded-full">Practical</span>
                        <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full">Competency Certificate</span>
                      </h3>
                      <p className="text-sm text-zinc-400 mt-1">
                        Practice real workplace scenarios in an interactive simulation. Make decisions, face consequences, and learn from outcomes.
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                        <span>15-20 minutes</span>
                        <span>•</span>
                        <span>Branching scenarios with NAVI coaching</span>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-zinc-500 group-hover:text-amber-400 transition-colors" />
                  </div>
                </button>
              </div>
              
              {/* Info Note */}
              <div className="mt-6 p-4 bg-zinc-800/50 rounded-lg">
                <p className="text-xs text-zinc-500">
                  <strong className="text-zinc-400">Note:</strong> Different assessment types award different certificate levels. 
                  NAVI Coach Me awards Intermediate certificates, MCQ/Grader awards Foundational certificates, and Simulated Labs award Competency certificates.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* NAVI Assistant Button */}
      <button
        onClick={() => setNaviOpen(!naviOpen)}
        className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-r from-[#1EBFB3] to-blue-500 rounded-full flex items-center justify-center shadow-lg hover:scale-105 transition-transform z-40"
        data-testid="navi-toggle"
      >
        {naviOpen ? (
          <X className="h-6 w-6 text-white" />
        ) : (
          <Brain className="h-6 w-6 text-white" />
        )}
      </button>

      {/* NAVI Chat Panel */}
      {naviOpen && (
        <div className="fixed bottom-24 right-6 w-96 h-[500px] bg-navigate-surface border border-[#1EBFB3]/30 rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-[#1EBFB3]/20 to-blue-500/20 border-b border-[#1EBFB3]/30 flex items-center gap-3">
            <img 
              src={NAVI_AVATAR} 
              alt="NAVI" 
              className="w-10 h-10 rounded-full object-cover"
            />
            <div>
              <h3 className="font-medium text-white">NAVI</h3>
              <p className="text-xs text-[#1EBFB3]">AI Learning Assistant</p>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {naviMessages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[80%] p-3 rounded-2xl text-sm ${
                  msg.role === 'user'
                    ? 'bg-navigate-gold text-black rounded-br-md'
                    : 'bg-zinc-800 text-white rounded-bl-md'
                }`}>
                  {msg.role === 'user' ? msg.content : formatNaviResponse(msg.content)}
                </div>
              </div>
            ))}
            {naviLoading && (
              <div className="flex justify-start">
                <div className="bg-zinc-800 p-3 rounded-2xl rounded-bl-md">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-[#1EBFB3] rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-[#1EBFB3] rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                    <div className="w-2 h-2 bg-[#1EBFB3] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={naviMessagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-zinc-800">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={naviInput}
                onChange={(e) => setNaviInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleNaviChat()}
                placeholder="Ask NAVI anything..."
                className="flex-1 bg-zinc-800 border border-zinc-700 rounded-full px-4 py-2 text-white text-sm focus:outline-none focus:border-[#1EBFB3]"
                disabled={naviLoading}
              />
              <button
                onClick={handleNaviChat}
                disabled={naviLoading || !naviInput.trim()}
                className="w-10 h-10 bg-gradient-to-r from-[#1EBFB3] to-blue-500 rounded-full flex items-center justify-center disabled:opacity-50"
              >
                <Send className="h-4 w-4 text-white" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* NAVI Coach Modal (Legacy) */}
      <NaviCoach
        isOpen={naviCoachOpen}
        onClose={() => setNaviCoachOpen(false)}
        courseId={actualCourseId}
        moduleId={null}
        moduleName={course?.overview?.title || 'Course'}
        token={token}
        onComplete={(assessment) => {
          toast.success('NAVI coaching session complete!', {
            description: 'Your assessment has been saved for instructor review.'
          });
        }}
      />
      
      {/* Enhanced Assessment Modal */}
      <NaviAssessment
        isOpen={naviAssessmentOpen}
        onClose={() => setNaviAssessmentOpen(false)}
        courseId={actualCourseId}
        courseTitle={course?.overview?.title || courseMetadata?.title || 'Course'}
        token={token}
        defaultMode={defaultAssessmentMode}
        userName={user?.full_name || user?.name || 'Learner'}
        userJobTitle={user?.job_title || 'Professional'}
        onComplete={(summary) => {
          if (summary?.passed && searchParams.get('programme')) {
            setNaviAssessmentOpen(false);
            setCurrentView('no-quiz-complete');
          }
          if (summary?.passed) {
            toast.success('Congratulations! You passed the assessment!', {
              description: summary.certificate_eligible 
                ? 'You are now eligible for your certificate.' 
                : 'Great work demonstrating your understanding.'
            });
          } else {
            toast.info('Assessment Complete', {
              description: 'Review the feedback to identify areas for improvement.'
            });
          }
        }}
      />

      {/* Full-Screen Assessment Questions Editor Modal */}
      {showQuestionsEditor && (
        <div className="fixed inset-0 bg-black/90 z-50 flex flex-col" data-testid="questions-editor-modal">
          {/* Modal Header */}
          <div className="bg-navigate-surface border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gradient-to-br from-[#1EBFB3] to-navigate-gold rounded-lg flex items-center justify-center">
                <HelpCircle className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Assessment Questions</h2>
                <p className="text-sm text-zinc-400">Define competency questions for learner assessment</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* AI Generate Button */}
              <button
                onClick={() => handleGenerateAIAssessment(5)}
                disabled={generatingAssessment || assessmentQuestions.length >= 10}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1EBFB3] to-navigate-gold text-white rounded-lg font-medium hover:from-[#1EBFB3] hover:to-yellow-500 disabled:opacity-50 transition-all"
                title="Generate competency-based questions using AI"
                data-testid="ai-generate-assessment-btn"
              >
                {generatingAssessment ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                {generatingAssessment ? 'Generating...' : 'AI Generate'}
              </button>
              <span className={`text-sm px-3 py-1 rounded-full ${
                assessmentQuestions.length > 0 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-zinc-700 text-zinc-400'
              }`}>
                {assessmentQuestions.length}/10 Questions
              </span>
              <button
                onClick={() => {
                  handleSaveQuestions();
                  setShowQuestionsEditor(false);
                }}
                disabled={savingQuestions}
                className="flex items-center gap-2 px-4 py-2 bg-navigate-gold text-black rounded-lg font-medium hover:bg-navigate-gold/90 disabled:opacity-50 transition-colors"
              >
                {savingQuestions ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save & Close
              </button>
              <button
                onClick={() => setShowQuestionsEditor(false)}
                className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Help Text */}
          <div className="bg-[#1EBFB3]/10 border-b border-[#1EBFB3]/20 px-6 py-3">
            <p className="text-sm text-[#1EBFB3]">
              <strong>Assessment Questions</strong> are used to evaluate learner competency through conversation. 
              These can be open-ended (AI graded), MCQ, or True/False (auto-graded). 
              Open-ended questions assess deep understanding; MCQ/TF test factual recall.
            </p>
          </div>

          {/* Modal Body - Scrollable */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-4xl mx-auto space-y-4">
              {assessmentQuestions.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-zinc-700 rounded-xl">
                  <HelpCircle className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400 mb-4">No assessment questions defined yet</p>
                  <p className="text-zinc-500 text-sm mb-6">Generate competency-based questions with AI or add them manually</p>
                  
                  {/* AI Generate - Primary Option */}
                  <button
                    onClick={() => handleGenerateAIAssessment(5)}
                    disabled={generatingAssessment}
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#1EBFB3] to-navigate-gold text-white rounded-lg hover:from-[#1EBFB3] hover:to-yellow-500 disabled:opacity-50 transition-all font-medium mx-auto mb-6"
                    data-testid="ai-generate-assessment-empty"
                  >
                    {generatingAssessment ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                    {generatingAssessment ? 'Generating Competency Questions...' : 'Generate with AI'}
                  </button>
                  
                  <p className="text-zinc-500 text-sm mb-4">or add manually:</p>
                  
                  <div className="flex items-center justify-center gap-3">
                    <button
                      onClick={() => handleAddQuestion('open')}
                      className="flex items-center gap-2 px-4 py-2 bg-[#1EBFB3]/20 text-[#1EBFB3] border border-[#1EBFB3]/30 rounded-lg hover:bg-[#1EBFB3]/30 transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                      Add Open-Ended
                    </button>
                    <button
                      onClick={() => handleAddQuestion('mcq')}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600/20 text-blue-400 border border-blue-500/30 rounded-lg hover:bg-blue-600/30 transition-colors"
                    >
                      <ListOrdered className="h-4 w-4" />
                      Add MCQ
                    </button>
                    <button
                      onClick={() => handleAddQuestion('truefalse')}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600/20 text-green-400 border border-green-500/30 rounded-lg hover:bg-green-600/30 transition-colors"
                    >
                      <CheckSquare className="h-4 w-4" />
                      Add True/False
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {assessmentQuestions.map((q, idx) => {
                    const question = typeof q === 'string' ? { type: 'open', question: q, options: [], correctAnswer: '' } : q;
                    return (
                      <div 
                        key={idx} 
                        className="bg-navigate-surface border border-zinc-800 rounded-xl overflow-hidden"
                        data-testid={`assessment-question-${idx}`}
                      >
                        {/* Question Header */}
                        <div className="flex items-center gap-3 px-4 py-3 bg-zinc-800/50 border-b border-zinc-700">
                          <div className="w-8 h-8 rounded-full bg-navigate-gold text-black flex items-center justify-center font-bold text-sm">
                            {idx + 1}
                          </div>
                          <div className="flex-1 flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              question.type === 'open' ? 'bg-[#1EBFB3]/20 text-[#1EBFB3]' :
                              question.type === 'mcq' ? 'bg-blue-500/20 text-blue-400' :
                              'bg-green-500/20 text-green-400'
                            }`}>
                              {question.type === 'open' ? 'Open-Ended (AI Graded)' : 
                               question.type === 'mcq' ? 'MCQ (Auto-Graded)' : 
                               'True/False (Auto-Graded)'}
                            </span>
                          </div>
                          <button
                            onClick={() => handleRemoveQuestion(idx)}
                            className="p-2 text-zinc-400 hover:text-red-400 hover:bg-zinc-700 rounded-lg transition-colors"
                            title="Remove question"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Question Content */}
                        <div className="p-4 space-y-4">
                          <div>
                            <label className="block text-sm text-zinc-400 mb-2">Question</label>
                            <Textarea
                              value={question.question}
                              onChange={(e) => handleQuestionChange(idx, 'question', e.target.value)}
                              className="bg-zinc-900 border-zinc-700 text-white min-h-[80px] focus:border-[#1EBFB3]"
                              placeholder="Enter your assessment question..."
                            />
                          </div>

                          {/* MCQ Options */}
                          {question.type === 'mcq' && (
                            <div className="space-y-3">
                              <label className="block text-sm text-zinc-400">Options (A, B, C, D)</label>
                              {['A', 'B', 'C', 'D'].map((letter, optIdx) => (
                                <div key={letter} className="flex items-center gap-3">
                                  <span className="w-6 h-6 bg-zinc-800 rounded-full flex items-center justify-center text-xs text-zinc-400">
                                    {letter}
                                  </span>
                                  <Input
                                    value={question.options?.[optIdx] || ''}
                                    onChange={(e) => handleMCQOptionChange(idx, optIdx, e.target.value)}
                                    className="flex-1 bg-zinc-900 border-zinc-700 text-white focus:border-blue-500"
                                    placeholder={`Option ${letter}...`}
                                  />
                                  <button
                                    onClick={() => handleQuestionChange(idx, 'correctAnswer', question.options?.[optIdx] || '')}
                                    className={`px-3 py-1.5 rounded text-xs transition-colors ${
                                      question.correctAnswer === question.options?.[optIdx]
                                        ? 'bg-green-500 text-white'
                                        : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                                    }`}
                                  >
                                    {question.correctAnswer === question.options?.[optIdx] ? 'Correct ✓' : 'Set Correct'}
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* True/False Options */}
                          {question.type === 'truefalse' && (
                            <div>
                              <label className="block text-sm text-zinc-400 mb-2">Correct Answer</label>
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={() => handleQuestionChange(idx, 'correctAnswer', 'True')}
                                  className={`flex-1 py-3 rounded-lg font-medium transition-colors ${
                                    question.correctAnswer === 'True'
                                      ? 'bg-green-500 text-white'
                                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                                  }`}
                                >
                                  True
                                </button>
                                <button
                                  onClick={() => handleQuestionChange(idx, 'correctAnswer', 'False')}
                                  className={`flex-1 py-3 rounded-lg font-medium transition-colors ${
                                    question.correctAnswer === 'False'
                                      ? 'bg-red-500 text-white'
                                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                                  }`}
                                >
                                  False
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Add Question Buttons */}
                  {assessmentQuestions.length < 10 && (
                    <div className="flex items-center justify-center gap-3 pt-4">
                      <button
                        onClick={() => handleAddQuestion('open')}
                        className="flex items-center gap-2 px-4 py-2 bg-[#1EBFB3]/20 text-[#1EBFB3] border border-[#1EBFB3]/30 rounded-lg hover:bg-[#1EBFB3]/30 transition-colors"
                      >
                        <Plus className="h-4 w-4" />
                        Add Open-Ended
                      </button>
                      <button
                        onClick={() => handleAddQuestion('mcq')}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600/20 text-blue-400 border border-blue-500/30 rounded-lg hover:bg-blue-600/30 transition-colors"
                      >
                        <ListOrdered className="h-4 w-4" />
                        Add MCQ
                      </button>
                      <button
                        onClick={() => handleAddQuestion('truefalse')}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600/20 text-green-400 border border-green-500/30 rounded-lg hover:bg-green-600/30 transition-colors"
                      >
                        <CheckSquare className="h-4 w-4" />
                        Add True/False
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Full-Screen Quiz Editor Modal */}
      {showQuizEditor && (
        <div className="fixed inset-0 bg-black/90 z-50 flex flex-col" data-testid="quiz-editor-modal">
          {/* Modal Header */}
          <div className="bg-navigate-surface border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-[#1EBFB3] rounded-lg flex items-center justify-center">
                <FileQuestion className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Course Quiz (MCQ)</h2>
                <p className="text-sm text-zinc-400">Standard multiple-choice quiz for factual recall</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* AI Generate Button */}
              <button
                onClick={() => handleGenerateAIQuiz(10)}
                disabled={generatingQuiz}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1EBFB3] to-blue-600 text-white rounded-lg font-medium hover:from-[#1EBFB3] hover:to-blue-500 disabled:opacity-50 transition-all"
                title="Generate quiz questions using AI"
                data-testid="ai-generate-quiz-btn"
              >
                {generatingQuiz ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                {generatingQuiz ? 'Generating...' : 'AI Generate'}
              </button>
              <span className={`text-sm px-3 py-1 rounded-full ${
                editedQuiz.length > 0 
                  ? 'bg-[#1EBFB3]/20 text-[#1EBFB3]' 
                  : 'bg-zinc-700 text-zinc-400'
              }`}>
                {editedQuiz.length} Questions
              </span>
              <button
                onClick={() => {
                  handleSaveQuiz();
                  setShowQuizEditor(false);
                }}
                disabled={savingQuiz}
                className="flex items-center gap-2 px-4 py-2 bg-navigate-gold text-black rounded-lg font-medium hover:bg-navigate-gold/90 disabled:opacity-50 transition-colors"
              >
                {savingQuiz ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save & Close
              </button>
              <button
                onClick={() => setShowQuizEditor(false)}
                className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Help Text */}
          <div className="bg-blue-500/10 border-b border-blue-500/20 px-6 py-3">
            <p className="text-sm text-blue-300">
              <strong>Course Quiz</strong> tests factual recall with auto-graded multiple-choice questions. 
              Learners must score 70% or higher to pass. This is separate from the Assessment which evaluates competency.
            </p>
          </div>

          {/* Modal Body - Scrollable */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-4xl mx-auto space-y-4">
              {editedQuiz.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-zinc-700 rounded-xl">
                  <FileQuestion className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400 mb-4">No quiz questions yet</p>
                  <p className="text-zinc-500 text-sm mb-6">Generate questions with AI or add them manually</p>
                  <div className="flex items-center justify-center gap-4">
                    <button
                      onClick={() => handleGenerateAIQuiz(10)}
                      disabled={generatingQuiz}
                      className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#1EBFB3] to-blue-600 text-white rounded-lg hover:from-[#1EBFB3] hover:to-blue-500 disabled:opacity-50 transition-all font-medium"
                      data-testid="ai-generate-quiz-empty"
                    >
                      {generatingQuiz ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Sparkles className="h-4 w-4" />
                      )}
                      {generatingQuiz ? 'Generating...' : 'Generate with AI'}
                    </button>
                    <span className="text-zinc-500">or</span>
                    <button
                      onClick={addQuizQuestion}
                      className="flex items-center gap-2 px-4 py-3 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                      Add Manually
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {editedQuiz.map((q, idx) => (
                    <div 
                      key={q.id || idx} 
                      className="bg-navigate-surface border border-zinc-800 rounded-xl overflow-hidden"
                      data-testid={`quiz-question-${idx}`}
                    >
                      {/* Question Header */}
                      <div className="flex items-center gap-3 px-4 py-3 bg-zinc-800/50 border-b border-zinc-700">
                        <div className="w-8 h-8 rounded-full bg-[#1EBFB3] text-white flex items-center justify-center font-bold text-sm">
                          {idx + 1}
                        </div>
                        <span className="flex-1 text-sm text-zinc-400">Multiple Choice Question</span>
                        <button
                          onClick={() => deleteQuizQuestion(idx)}
                          className="p-2 text-zinc-400 hover:text-red-400 hover:bg-zinc-700 rounded-lg transition-colors"
                          title="Remove question"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Question Content */}
                      <div className="p-4 space-y-4">
                        <div>
                          <label className="block text-sm text-zinc-400 mb-2">Question</label>
                          <Textarea
                            value={q.question}
                            onChange={(e) => updateQuizQuestion(idx, 'question', e.target.value)}
                            className="bg-zinc-900 border-zinc-700 text-white min-h-[80px] focus:border-[#1EBFB3]"
                            placeholder="Enter your quiz question..."
                          />
                        </div>

                        {/* Options */}
                        <div className="space-y-3">
                          <label className="block text-sm text-zinc-400">Answer Options</label>
                          {['A', 'B', 'C', 'D'].map((letter, optIdx) => (
                            <div key={letter} className="flex items-center gap-3">
                              <button
                                onClick={() => updateQuizQuestion(idx, 'correctAnswer', optIdx)}
                                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                                  q.correctAnswer === optIdx
                                    ? 'bg-green-500 text-white'
                                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                                }`}
                                title={q.correctAnswer === optIdx ? 'Correct answer' : 'Click to set as correct'}
                              >
                                {letter}
                              </button>
                              <Input
                                value={q.options?.[optIdx] || ''}
                                onChange={(e) => updateQuizOption(idx, optIdx, e.target.value)}
                                className="flex-1 bg-zinc-900 border-zinc-700 text-white focus:border-[#1EBFB3]"
                                placeholder={`Option ${letter}...`}
                              />
                              {q.correctAnswer === optIdx && (
                                <span className="text-xs text-green-400 bg-green-500/20 px-2 py-1 rounded">Correct</span>
                              )}
                            </div>
                          ))}
                          <p className="text-xs text-zinc-500 mt-2">
                            Click the letter button to mark the correct answer
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Add Question Button */}
                  <div className="flex justify-center pt-4">
                    <button
                      onClick={addQuizQuestion}
                      className="flex items-center gap-2 px-6 py-3 bg-[#1EBFB3]/20 text-[#1EBFB3] border border-[#1EBFB3]/30 rounded-lg hover:bg-[#1EBFB3]/30 transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                      Add Another Question
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Overview Section Component
function OverviewSection({ overview, onStart, editMode, onUpdateOverview, previewMode, materials, thumbnailUrl }) {
  return (
    <div className="p-8 max-w-4xl mx-auto">
      {/* Hero with Thumbnail */}
      <div className="relative bg-gradient-to-r from-zinc-800/80 to-zinc-700/50 rounded-2xl mb-8 border border-zinc-700 overflow-hidden">
        {/* Thumbnail as header image */}
        {thumbnailUrl && (
          <div className="absolute inset-0">
            <img 
              src={thumbnailUrl.startsWith('http') ? thumbnailUrl : `${API_BASE}${thumbnailUrl}`}
              alt={overview?.title || 'Course thumbnail'}
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/80 to-transparent" />
          </div>
        )}
        
        <div className="relative p-8">
          {editMode && (
            <div className="mb-4 pb-4 border-b border-zinc-700">
              <Input
                value={overview?.title || ''}
                onChange={(e) => onUpdateOverview && onUpdateOverview('title', e.target.value)}
                className="font-heading text-3xl font-bold text-white bg-transparent border-[#1EBFB3] mb-2"
                placeholder="Course title..."
              />
              <div className="flex items-center gap-2 mt-2">
                <Clock className="h-4 w-4 text-zinc-400" />
                <Input
                  value={overview?.estimated_duration || ''}
                  onChange={(e) => onUpdateOverview && onUpdateOverview('estimated_duration', e.target.value)}
                  className="w-32 bg-transparent border-[#1EBFB3] text-sm"
                  placeholder="Duration..."
                />
              </div>
            </div>
          )}
          
          {!editMode && (
            <>
              <h1 className="font-heading text-4xl font-bold text-white mb-4">
                {overview?.title}
              </h1>
              <div className="flex items-center gap-4 text-zinc-300">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  {overview?.estimated_duration}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Learning Objectives */}
      <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Target className="h-6 w-6 text-navigate-gold" />
          <h2 className="text-xl font-bold text-white">Learning Objectives</h2>
        </div>
        <ul className="space-y-3">
          {overview?.learning_objectives?.map((obj, idx) => (
            <li key={idx} className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-400 shrink-0 mt-0.5" />
              <span className="text-zinc-300">{obj}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Target Audience */}
      <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Users className="h-6 w-6 text-navigate-gold" />
          <h2 className="text-xl font-bold text-white">Who Is This For?</h2>
        </div>
        <p className="text-zinc-300">{overview?.target_audience}</p>
      </div>

      {/* Expected Outcomes */}
      <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6 mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Award className="h-6 w-6 text-navigate-gold" />
          <h2 className="text-xl font-bold text-white">Expected Outcomes</h2>
        </div>
        <ul className="space-y-3">
          {overview?.expected_outcomes?.map((outcome, idx) => (
            <li key={idx} className="flex items-start gap-3">
              <span className="w-6 h-6 bg-navigate-gold/20 rounded-full flex items-center justify-center text-navigate-gold text-sm shrink-0">
                {idx + 1}
              </span>
              <span className="text-zinc-300">{outcome}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Supplementary Materials (Student View) */}
      {materials && materials.length > 0 && (
        <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6 mb-8" data-testid="student-materials-section">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="h-6 w-6 text-navigate-gold" />
            <h2 className="text-xl font-bold text-white">Study Materials</h2>
          </div>
          <p className="text-zinc-400 text-sm mb-4">
            Download supplementary materials to enhance your learning experience.
          </p>
          <div className="grid gap-3">
            {materials.map((material) => (
              <a
                key={material.material_id}
                href={`${API_BASE}${material.url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg hover:bg-zinc-700/50 transition-colors group"
                data-testid={`student-material-${material.material_id}`}
              >
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center text-xl">
                  {material.file_type === 'pdf' ? '📄' :
                   material.file_type === 'doc' || material.file_type === 'docx' ? '📝' :
                   material.file_type === 'ppt' || material.file_type === 'pptx' ? '📊' :
                   material.file_type === 'xls' || material.file_type === 'xlsx' ? '📈' : '📎'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{material.title}</p>
                  <p className="text-xs text-zinc-500">
                    {material.file_type?.toUpperCase()} • {material.file_size < 1024 * 1024 
                      ? `${(material.file_size / 1024).toFixed(1)} KB`
                      : `${(material.file_size / (1024 * 1024)).toFixed(1)} MB`}
                  </p>
                </div>
                <Download className="h-5 w-5 text-zinc-400 group-hover:text-navigate-gold transition-colors" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Start Button */}
      <div className="flex justify-center">
        <button
          onClick={onStart}
          className="px-8 py-4 bg-navigate-gold text-black rounded-xl font-medium hover:bg-navigate-gold-light transition-colors flex items-center gap-3 text-lg"
        >
          <BookOpen className="h-5 w-5" />
          Start Learning
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

// Content Section Component with Collapsible Subsections
function ContentSection({ section, sectionNumber, totalSections, onNext, onPrev, isFirst, isLast, noQuiz = false, courseId, token, onImageUpload, editMode = false, onUpdateContent, onAIGenerateSubsection, generatingSubsection, courseMetadata, onAIGenerateSectionImage, courseThumbnail }) {
  const headerImage = section.image_url || courseThumbnail || null;
  const [sectionImage, setSectionImage] = useState(headerImage);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [expandedSubsections, setExpandedSubsections] = useState(new Set([0])); // First subsection expanded by default
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const fileInputRef = useRef(null);
  
  // Image cropping state
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [focalPoint, setFocalPoint] = useState(section.image_focal_point || 'center');
  
  // Media Library picker state
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  
  // Embedded media state (images, infographics, videos within content)
  const [embeddedMedia, setEmbeddedMedia] = useState(section.embedded_media || []);
  const [showEmbedMediaPicker, setShowEmbedMediaPicker] = useState(false);
  
  // Lightbox state for expanding images
  const [lightboxImage, setLightboxImage] = useState(null);
  
  // Keyboard support for lightbox (Escape to close)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && lightboxImage) {
        setLightboxImage(null);
      }
    };
    
    if (lightboxImage) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when lightbox is open
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [lightboxImage]);
  
  // Update sectionImage when section changes or course thumbnail changes
  useEffect(() => {
    setSectionImage(section.image_url || courseThumbnail || null);
    setFocalPoint(section.image_focal_point || 'center');
    setEmbeddedMedia(section.embedded_media || []);
  }, [section.section_id, section.image_url, section.image_focal_point, section.embedded_media, courseThumbnail]);
  
  // Local state for editing content - this allows paste to work properly
  const [localKeyTakeaways, setLocalKeyTakeaways] = useState((section.key_takeaways || []).join('\n'));
  const [localPracticalExamples, setLocalPracticalExamples] = useState((section.practical_examples || []).join('\n\n'));
  const [generatingImage, setGeneratingImage] = useState(false);
  
  // Per-subsection editing state
  const [editableSubsections, setEditableSubsections] = useState([]);
  
  // Parse content into editable subsections
  const parseContentToEditableSubsections = (content) => {
    if (!content) return [{ title: 'Section Content', content: '', isIntro: true, nestedSubsections: [] }];
    
    // Normalize escaped newlines before parsing
    const normalizedContent = content
      .replace(/\\n\\n/g, '\n\n')  // Double escaped newlines
      .replace(/\\n/g, '\n')       // Single escaped newlines
      .replace(/\\t/g, '  ')       // Tabs to spaces
      .replace(/\r\n/g, '\n');     // Windows line endings
    
    const lines = normalizedContent.split('\n');
    const subsections = [];
    let currentSubsection = null;
    let currentNestedSubsection = null;
    let introContent = [];
    
    lines.forEach((line) => {
      const trimmedLine = line.trim();
      
      // Nested headers (### N.M Title) - start new nested subsection
      const nestedMatch = trimmedLine.match(/^###\s*(\d+\.?\d*)\s+(.+)$/);
      if (nestedMatch && currentSubsection) {
        // Save previous nested subsection
        if (currentNestedSubsection) {
          if (!currentSubsection.nestedSubsections) {
            currentSubsection.nestedSubsections = [];
          }
          currentSubsection.nestedSubsections.push({
            ...currentNestedSubsection,
            content: Array.isArray(currentNestedSubsection.content) 
              ? currentNestedSubsection.content.join('\n') 
              : currentNestedSubsection.content
          });
        }
        
        const nestedTitle = nestedMatch[2];
        currentNestedSubsection = { title: nestedTitle, content: [] };
        return;
      }
      
      // Main headers (## or **Title**)  - start new subsection
      if (trimmedLine.startsWith('## ') || (trimmedLine.match(/^\*\*[^*]+\*\*$/) && !trimmedLine.includes(':'))) {
        // Save previous nested subsection to parent
        if (currentNestedSubsection && currentSubsection) {
          if (!currentSubsection.nestedSubsections) {
            currentSubsection.nestedSubsections = [];
          }
          currentSubsection.nestedSubsections.push({
            ...currentNestedSubsection,
            content: Array.isArray(currentNestedSubsection.content) 
              ? currentNestedSubsection.content.join('\n') 
              : currentNestedSubsection.content
          });
          currentNestedSubsection = null;
        }
        
        // Save previous subsection
        if (currentSubsection) {
          subsections.push(currentSubsection);
        } else if (introContent.length > 0) {
          subsections.push({
            title: 'Overview',
            content: introContent.join('\n'),
            isIntro: true,
            nestedSubsections: []
          });
        }
        
        const title = trimmedLine.replace(/^##\s*/, '').replace(/^\*\*/, '').replace(/\*\*$/, '');
        currentSubsection = { title, content: [], isIntro: false, nestedSubsections: [] };
        currentNestedSubsection = null;
      }
      // Regular content
      else {
        if (currentNestedSubsection) {
          currentNestedSubsection.content.push(line);
        } else if (currentSubsection) {
          currentSubsection.content.push(line);
        } else {
          introContent.push(line);
        }
      }
    });
    
    // Save final nested subsection
    if (currentNestedSubsection && currentSubsection) {
      if (!currentSubsection.nestedSubsections) {
        currentSubsection.nestedSubsections = [];
      }
      currentSubsection.nestedSubsections.push({
        ...currentNestedSubsection,
        content: Array.isArray(currentNestedSubsection.content) 
          ? currentNestedSubsection.content.join('\n') 
          : currentNestedSubsection.content
      });
    }
    
    // Push final subsection or intro
    if (currentSubsection) {
      subsections.push(currentSubsection);
    } else if (introContent.length > 0) {
      subsections.push({
        title: 'Content',
        content: introContent.join('\n'),
        isIntro: true,
        nestedSubsections: []
      });
    }
    
    // Convert content arrays to strings
    return subsections.length > 0 ? subsections.map(s => ({
      ...s,
      content: Array.isArray(s.content) ? s.content.join('\n') : s.content,
      nestedSubsections: s.nestedSubsections || []
    })) : [{ title: 'Section Content', content: '', isIntro: true, nestedSubsections: [] }];
  };
  
  // Reconstruct full content from editable subsections
  const reconstructContent = (subsections) => {
    return subsections.map((sub, idx) => {
      let content = '';
      if (sub.isIntro) {
        content = sub.content;
      } else {
        content = `## ${sub.title}\n${sub.content}`;
      }
      
      // Add nested subsections (e.g., ### 2.1 Title)
      if (sub.nestedSubsections && sub.nestedSubsections.length > 0) {
        const nestedContent = sub.nestedSubsections.map((nested, nestedIdx) => {
          const number = `${idx + 1}.${nestedIdx + 1}`;
          return `### ${number} ${nested.title}\n${nested.content}`;
        }).join('\n\n');
        content += '\n\n' + nestedContent;
      }
      
      return content;
    }).join('\n\n');
  };
  
  // Track if we're in the middle of syncing to prevent infinite loops
  const [isSyncing, setIsSyncing] = useState(false);
  const sectionIdRef = useRef(section.section_id || section.title);
  
  // Sync local state when section prop changes (e.g., switching sections)
  // Only re-parse when section_id changes, not when content changes from our own edits
  useEffect(() => {
    const currentId = section.section_id || section.title || '';
    if (currentId !== sectionIdRef.current) {
      sectionIdRef.current = currentId;
      setIsSyncing(true);
      const parsed = parseContentToEditableSubsections(section.content || '');
      setEditableSubsections(parsed.length > 0 ? parsed : [{ title: 'Introduction', content: '', isIntro: true }]);
      setLocalKeyTakeaways((section.key_takeaways || []).join('\n'));
      setLocalPracticalExamples((section.practical_examples || []).join('\n\n'));
      // Reset syncing flag after state updates
      setTimeout(() => setIsSyncing(false), 0);
    }
  }, [section.section_id, section.title, section.content, section.key_takeaways, section.practical_examples]);
  
  // Initialize on first mount
  useEffect(() => {
    const parsed = parseContentToEditableSubsections(section.content || '');
    setEditableSubsections(parsed.length > 0 ? parsed : [{ title: 'Introduction', content: '', isIntro: true }]);
    setLocalKeyTakeaways((section.key_takeaways || []).join('\n'));
    setLocalPracticalExamples((section.practical_examples || []).join('\n\n'));
  }, []); // Only run once on mount
  
  // Update parent state when editable subsections change
  useEffect(() => {
    if (editMode && onUpdateContent && editableSubsections.length > 0 && !isSyncing) {
      const fullContent = reconstructContent(editableSubsections);
      onUpdateContent('content', fullContent);
    }
  }, [editableSubsections, editMode, isSyncing]);
  
  // Update a specific subsection's content
  const updateSubsectionContent = (index, newContent) => {
    setEditableSubsections(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], content: newContent };
      return updated;
    });
  };
  
  // Update a specific subsection's title
  const updateSubsectionTitle = (index, newTitle) => {
    setEditableSubsections(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], title: newTitle };
      return updated;
    });
  };
  
  // Update nested subsection title (e.g., 2.1, 2.2)
  const updateNestedSubsectionTitle = (parentIndex, nestedIndex, newTitle) => {
    setEditableSubsections(prev => {
      const updated = [...prev];
      const parent = { ...updated[parentIndex] };
      const nested = [...(parent.nestedSubsections || [])];
      nested[nestedIndex] = { ...nested[nestedIndex], title: newTitle };
      parent.nestedSubsections = nested;
      updated[parentIndex] = parent;
      return updated;
    });
  };
  
  // Update nested subsection content
  const updateNestedSubsectionContent = (parentIndex, nestedIndex, newContent) => {
    setEditableSubsections(prev => {
      const updated = [...prev];
      const parent = { ...updated[parentIndex] };
      const nested = [...(parent.nestedSubsections || [])];
      nested[nestedIndex] = { ...nested[nestedIndex], content: newContent };
      parent.nestedSubsections = nested;
      updated[parentIndex] = parent;
      return updated;
    });
  };
  
  // Add a new subsection
  const addSubsection = () => {
    setEditableSubsections(prev => [
      ...prev,
      { title: 'New Subsection', content: '', isIntro: false, nestedSubsections: [] }
    ]);
  };
  
  // Add a nested subsection (e.g., 2.1, 2.2 under section 2)
  const addNestedSubsection = (parentIndex) => {
    setEditableSubsections(prev => {
      const updated = [...prev];
      const parent = { ...updated[parentIndex] };
      const nested = [...(parent.nestedSubsections || [])];
      const nestedNumber = nested.length + 1;
      nested.push({ 
        title: `Subtopic ${parentIndex + 1}.${nestedNumber}`, 
        content: '' 
      });
      parent.nestedSubsections = nested;
      updated[parentIndex] = parent;
      return updated;
    });
  };
  
  // Remove a nested subsection
  const removeNestedSubsection = (parentIndex, nestedIndex) => {
    setEditableSubsections(prev => {
      const updated = [...prev];
      const parent = { ...updated[parentIndex] };
      const nested = [...(parent.nestedSubsections || [])];
      nested.splice(nestedIndex, 1);
      parent.nestedSubsections = nested;
      updated[parentIndex] = parent;
      return updated;
    });
  };
  
  // Remove a subsection
  const removeSubsection = (index) => {
    if (editableSubsections.length <= 1) {
      toast.error('Cannot remove the last subsection');
      return;
    }
    setEditableSubsections(prev => prev.filter((_, i) => i !== index));
  };
  
  const embeddedMediaRef = useRef(embeddedMedia);
  embeddedMediaRef.current = embeddedMedia;
  const saveEmbeddedMedia = (updated) => {
    embeddedMediaRef.current = updated;
    setEmbeddedMedia(updated);
    if (onUpdateContent) onUpdateContent('embedded_media', updated);
  };

  // Uses the ref so several files finishing together don't overwrite each other
  const addEmbeddedMedia = (media) => {
    saveEmbeddedMedia([...embeddedMediaRef.current, { ...media, id: `embed_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, caption: '' }]);
  };

  useEffect(() => {
    if (editMode && onUpdateContent && !isSyncing) {
      const takeaways = localKeyTakeaways.split('\n').filter(t => t.trim());
      onUpdateContent('key_takeaways', takeaways);
    }
  }, [localKeyTakeaways, editMode, isSyncing]);
  
  useEffect(() => {
    if (editMode && onUpdateContent && !isSyncing) {
      const examples = localPracticalExamples.split('\n\n').filter(ex => ex.trim());
      onUpdateContent('practical_examples', examples);
    }
  }, [localPracticalExamples, editMode, isSyncing]);
  
  // Helper function to get full image URL with token
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    const API = API_BASE;
    
    // If it's a private API files path, convert to public endpoint
    if (imageUrl.startsWith('/api/files/')) {
      // Convert /api/files/xxx to /api/public/files/xxx for public access
      const publicPath = imageUrl.replace('/api/files/', '/api/public/files/');
      return `${API}${publicPath}`;
    }
    
    // If it's already using public endpoint
    if (imageUrl.startsWith('/api/public/')) {
      return `${API}${imageUrl}`;
    }
    
    // If it's any other API path
    if (imageUrl.startsWith('/api/')) {
      return `${API}${imageUrl}`;
    }
    
    // If already a full URL, return as-is
    return imageUrl;
  };

  const toggleSubsection = (index) => {
    setExpandedSubsections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    const allIndices = new Set(Array.from({ length: 20 }, (_, i) => i));
    setExpandedSubsections(allIndices);
  };

  const collapseAll = () => {
    setExpandedSubsections(new Set());
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    // Create a preview URL and open the cropper
    const previewUrl = URL.createObjectURL(file);
    setImageToCrop({ file, previewUrl });
    setShowCropper(true);
  };
  
  // Handle crop completion - upload the cropped image
  const handleCropComplete = async (croppedResult) => {
    setShowCropper(false);
    
    if (!imageToCrop) return;
    
    setUploadingImage(true);
    const formData = new FormData();
    
    // Use cropped blob if available, otherwise use original file
    if (croppedResult?.blob) {
      formData.append('file', croppedResult.blob, 'cropped-image.jpg');
    } else {
      formData.append('file', imageToCrop.file);
    }
    formData.append('section_index', sectionNumber - 1);
    
    try {
      const response = await axios.post(
        `${API}/ai/course/${courseId}/section-image`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setSectionImage(response.data.image_url);
      toast.success('Image uploaded successfully');
      if (onImageUpload) onImageUpload(sectionNumber - 1, response.data.image_url);
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
      // Clean up preview URL
      if (imageToCrop?.previewUrl) {
        URL.revokeObjectURL(imageToCrop.previewUrl);
      }
      setImageToCrop(null);
    }
  };
  
  // Handle crop cancel
  const handleCropCancel = () => {
    setShowCropper(false);
    if (imageToCrop?.previewUrl) {
      URL.revokeObjectURL(imageToCrop.previewUrl);
    }
    setImageToCrop(null);
  };
  
  // Open cropper for existing image
  const openCropperForExisting = () => {
    const imageUrl = getImageUrl(sectionImage);
    if (imageUrl) {
      setImageToCrop({ file: null, previewUrl: imageUrl, isExisting: true });
      setShowCropper(true);
    }
  };
  
  // Get object position style based on focal point
  const getObjectPosition = () => {
    const positionMap = {
      'top-left': 'left top',
      'top': 'center top',
      'top-right': 'right top',
      'left': 'left center',
      'center': 'center center',
      'right': 'right center',
      'bottom-left': 'left bottom',
      'bottom': 'center bottom',
      'bottom-right': 'right bottom',
    };
    return positionMap[focalPoint] || 'center center';
  };

  // Parse content into collapsible subsections
  const parseContentToSubsections = (content) => {
    if (!content) return [];
    
    // Normalize escaped newlines before parsing
    const normalizedContent = content
      .replace(/\\n\\n/g, '\n\n')  // Double escaped newlines
      .replace(/\\n/g, '\n')       // Single escaped newlines
      .replace(/\\t/g, '  ')       // Tabs to spaces
      .replace(/\r\n/g, '\n');     // Windows line endings
    
    const lines = normalizedContent.split('\n');
    const subsections = [];
    let currentSubsection = null;
    let introContent = [];
    
    lines.forEach((line) => {
      const trimmedLine = line.trim();
      
      // Main headers (## or **Title**)  - start new subsection
      if (trimmedLine.startsWith('## ') || (trimmedLine.match(/^\*\*[^*]+\*\*$/) && !trimmedLine.includes(':'))) {
        // Save previous subsection
        if (currentSubsection) {
          subsections.push(currentSubsection);
        } else if (introContent.length > 0) {
          subsections.push({
            title: 'Overview',
            content: introContent.join('\n'),
            isIntro: true
          });
        }
        
        const title = trimmedLine.replace(/^##\s*/, '').replace(/^\*\*/, '').replace(/\*\*$/, '');
        currentSubsection = { title, content: [], isIntro: false };
      }
      // Subheaders within a subsection
      else if (trimmedLine.startsWith('### ') || trimmedLine.match(/^\d+\.\s*\*\*[^*]+\*\*/)) {
        if (currentSubsection) {
          currentSubsection.content.push(line);
        } else {
          introContent.push(line);
        }
      }
      // Regular content
      else {
        if (currentSubsection) {
          currentSubsection.content.push(line);
        } else {
          introContent.push(line);
        }
      }
    });
    
    // Push final subsection or intro
    if (currentSubsection) {
      subsections.push(currentSubsection);
    } else if (introContent.length > 0) {
      subsections.push({
        title: 'Content',
        content: introContent.join('\n'),
        isIntro: true
      });
    }
    
    // Convert content arrays to strings
    return subsections.map(s => ({
      ...s,
      content: Array.isArray(s.content) ? s.content.join('\n') : s.content
    }));
  };

  const subsections = parseContentToSubsections(section.content);

  return (
    <div className="p-8 max-w-4xl mx-auto">
      {editMode && <SectionFactFlags flags={section.fact_flags} />}
      {/* Progress Header */}
      <div className="flex items-center justify-between mb-8">
        <span className="text-sm text-zinc-400">
          Section {sectionNumber} of {totalSections}
        </span>
        <div className="flex items-center gap-1">
          {Array.from({ length: totalSections }).map((_, idx) => (
            <div
              key={idx}
              className={`w-2 h-2 rounded-full ${
                idx < sectionNumber ? 'bg-navigate-gold' : 'bg-zinc-700'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Section Image with Upload and Crop Options */}
      <div data-testid="section-header-image" className={`relative w-full aspect-video bg-zinc-900 rounded-2xl mb-8 overflow-hidden group shadow-2xl shadow-black/40 ${editMode ? 'ring-2 ring-[#1EBFB3] ring-offset-2 ring-offset-navigate-dark' : ''}`}>
        {sectionImage ? (
          <button
            onClick={() => !editMode && setLightboxImage({
              url: getImageUrl(sectionImage),
              caption: section.title,
              filename: `${section.title} - Section Image`
            })}
            className={`w-full h-full block ${!editMode ? 'cursor-zoom-in' : ''}`}
            disabled={editMode}
          >
            <img 
              src={getImageUrl(sectionImage)} 
              alt={section.title}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-contain"
              style={{ objectPosition: getObjectPosition() }}
              onError={(e) => {
                console.error('Image load failed:', sectionImage);
                // Show fallback placeholder instead of hiding
                e.target.onerror = null;
                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="200" viewBox="0 0 400 200"><rect fill="%23374151" width="400" height="200"/><text x="50%" y="45%" fill="%239CA3AF" font-family="Arial" font-size="14" text-anchor="middle">Image not available</text><text x="50%" y="60%" fill="%23A78BFA" font-family="Arial" font-size="12" text-anchor="middle">Upload a Course Image above</text></svg>';
              }}
            />
            {/* Expand indicator for learners on section header image */}
            {!editMode && (
              <div className="absolute inset-0 bg-black/0 hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
                <div className="bg-black/60 rounded-full p-4">
                  <Maximize2 className="h-8 w-8 text-white" />
                </div>
              </div>
            )}
          </button>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-zinc-500">
            <ImageIcon className="h-12 w-12 mb-2" />
            <p className="text-sm mb-3">No section image</p>
            {editMode && (
              <p className="text-xs text-[#1EBFB3]">Generate one with AI or upload your own</p>
            )}
          </div>
        )}
        
        {/* Edit Mode indicator - shows when course thumbnail is being used */}
        {editMode && courseThumbnail && !section.image_url && (
          <div className="absolute top-3 right-3 bg-[#1EBFB3] text-white text-xs px-2 py-1 rounded font-medium">
            Course Image
          </div>
        )}
        
        {/* Upload/Edit overlay - only show if no course thumbnail (for backwards compatibility) */}
        {editMode && (
          <div className={`absolute inset-0 bg-black/50 flex items-center justify-center gap-3 transition-opacity ${sectionImage ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'}`} data-testid="section-image-edit-overlay">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
            
            {/* Main button - always opens Media Library for consistency */}
            <button
              onClick={() => setShowMediaLibrary(true)}
              disabled={uploadingImage}
              className="flex items-center gap-2 px-4 py-2 bg-navigate-gold text-black rounded-lg font-medium hover:bg-navigate-gold-light transition-colors disabled:opacity-50"
            >
              {uploadingImage ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <ImageIcon className="h-4 w-4" />
                  {sectionImage ? 'Change' : 'Select Image'}
                </>
              )}
            </button>
            
            {/* Crop button for existing image */}
            {sectionImage && editMode && (
              <button
                onClick={openCropperForExisting}
                className="flex items-center gap-2 px-4 py-2 bg-zinc-700 text-white rounded-lg font-medium hover:bg-zinc-600 transition-colors"
              >
                <Crop className="h-4 w-4" />
                Crop
              </button>
            )}
            
            {/* Direct upload button (secondary option) */}
            {editMode && (
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingImage}
                className="flex items-center gap-2 px-4 py-2 bg-zinc-700 text-white rounded-lg font-medium hover:bg-zinc-600 transition-colors disabled:opacity-50"
                title="Upload directly from your device"
              >
                <Upload className="h-4 w-4" />
                Upload New
              </button>
            )}
            
            {/* AI Generate Image button */}
            {editMode && onAIGenerateSectionImage && (
              <button
                onClick={async () => {
                  setGeneratingImage(true);
                try {
                  await onAIGenerateSectionImage(sectionNumber - 1);
                } finally {
                  setGeneratingImage(false);
                }
              }}
              disabled={generatingImage}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1EBFB3] to-blue-600 text-white rounded-lg font-medium hover:from-[#1EBFB3] hover:to-blue-500 transition-colors disabled:opacity-50"
              data-testid="ai-generate-section-image-btn"
            >
              {generatingImage ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  {section.image_url ? 'Regenerate with AI' : 'AI Generate'}
                </>
              )}
            </button>
          )}
          </div>
        )}
        
        {/* Focal Point Control (Edit Mode Only) - only show if no course thumbnail */}
        {sectionImage && editMode && !courseThumbnail && (
          <div className="absolute bottom-2 right-2 bg-zinc-900/90 p-2 rounded-lg border border-zinc-700">
            <p className="text-[10px] text-zinc-400 mb-1 text-center">Focal Point</p>
            <FocalPointSelector 
              value={focalPoint} 
              onChange={(newFocal) => {
                setFocalPoint(newFocal);
                if (onUpdateContent) {
                  onUpdateContent('image_focal_point', newFocal);
                }
              }} 
            />
          </div>
        )}
        
        {editMode && !courseThumbnail && (
          <div className="absolute top-2 right-2 bg-[#1EBFB3] text-white text-xs px-2 py-1 rounded-full">
            Edit Mode
          </div>
        )}
      </div>
      
      {/* Image Cropper Modal */}
      {showCropper && imageToCrop && (
        <ImageCropper
          imageUrl={imageToCrop.previewUrl}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          aspectRatio={16/9}
        />
      )}
      
      {/* Media Library Picker Modal */}
      {showMediaLibrary && (
        <MediaLibraryPicker 
          token={token}
          onSelect={async (media) => {
            // Use the media URL from library
            const imageUrl = media.url?.startsWith('/api/') 
              ? `${API_BASE}${media.url}`
              : media.url;
            
            // Update the section image in the database
            try {
              await axios.put(
                `${API_BASE}/api/ai/course/${courseId}/section-image-url`,
                {
                  section_index: sectionNumber - 1,
                  image_url: media.url  // Store the relative URL
                },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              
              setSectionImage(imageUrl);
              if (onImageUpload) {
                onImageUpload(sectionNumber - 1, imageUrl);
              }
              toast.success('Image selected from library');
            } catch (error) {
              console.error('Failed to update image:', error);
              toast.error('Failed to set image');
            }
            
            setShowMediaLibrary(false);
          }}
          onClose={() => setShowMediaLibrary(false)}
        />
      )}

      {/* Section Title - Editable */}
      {editMode ? (
        <div className="mb-8 pb-4 border-b border-zinc-800">
          <Input
            value={section.title}
            onChange={(e) => onUpdateContent && onUpdateContent('title', e.target.value)}
            className="font-heading text-3xl font-bold text-white bg-transparent border-[#1EBFB3] focus:border-[#1EBFB3]"
            placeholder="Section title..."
          />
        </div>
      ) : (
        <h1 className="font-heading text-3xl font-bold text-white mb-8 pb-4 border-b border-zinc-800">
          {section.title}
        </h1>
      )}

      {/* Content Area */}
      {editMode ? (
        // Edit Mode: Per-subsection editing with rich text toolbar
        <div className="mb-8 space-y-6">
          <div className="flex items-center justify-between mb-4">
            <label className="block text-sm font-medium text-[#1EBFB3]">
              Edit Content by Subsection
            </label>
            <button
              onClick={addSubsection}
              className="flex items-center gap-2 px-3 py-1.5 bg-[#1EBFB3] text-white text-sm rounded-lg hover:bg-[#1EBFB3] transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add Subsection
            </button>
          </div>
          
          {editableSubsections.map((subsection, idx) => (
            <div 
              key={idx} 
              className="border border-zinc-700 rounded-xl overflow-hidden bg-zinc-800/30"
            >
              {/* Subsection Header */}
              <div className="flex items-center gap-3 p-4 bg-zinc-800 border-b border-zinc-700">
                <div className="w-8 h-8 rounded-full bg-navigate-gold text-black flex items-center justify-center font-bold text-sm shrink-0">
                  {idx + 1}
                </div>
                <Input
                  value={subsection.title}
                  onChange={(e) => updateSubsectionTitle(idx, e.target.value)}
                  className="flex-1 bg-zinc-900 border-zinc-600 text-white font-semibold focus:border-[#1EBFB3]"
                  placeholder="Subsection title..."
                />
                {onAIGenerateSubsection && (
                  <button
                    onClick={() => onAIGenerateSubsection(idx, subsection.title, (content) => updateSubsectionContent(idx, content))}
                    disabled={generatingSubsection === idx}
                    className="p-2 text-[#1EBFB3] hover:text-[#1EBFB3] hover:bg-[#1EBFB3]/10 rounded-lg transition-colors disabled:opacity-50"
                    title="AI Generate Content"
                    data-testid={`ai-generate-subsection-${idx}`}
                  >
                    {generatingSubsection === idx ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Wand2 className="h-4 w-4" />
                    )}
                  </button>
                )}
                <button
                  onClick={() => addNestedSubsection(idx)}
                  className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition-colors"
                  title={`Add sub-section (${idx + 1}.1, ${idx + 1}.2, etc.)`}
                >
                  <Plus className="h-4 w-4" />
                </button>
                {editableSubsections.length > 1 && (
                  <button
                    onClick={() => removeSubsection(idx)}
                    className="p-2 text-zinc-400 hover:text-red-400 hover:bg-zinc-700 rounded-lg transition-colors"
                    title="Remove subsection"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              
              {/* Subsection Content Editor */}
              <div className="p-4">
                <RichTextEditor
                  value={subsection.content}
                  onChange={(newContent) => updateSubsectionContent(idx, newContent)}
                  placeholder={`Enter content for "${subsection.title}"...\n\nUse the toolbar above for formatting:\n• Bold (Ctrl+B)\n• Italic (Ctrl+I)\n• Underline (Ctrl+U)\n• Bullet/Numbered lists`}
                  minHeight="150px"
                  testId={`subsection-editor-${idx}`}
                />
              </div>
              
              {/* Nested Sub-sections (2.1, 2.2, etc.) */}
              {subsection.nestedSubsections && subsection.nestedSubsections.length > 0 && (
                <div className="px-4 pb-4 space-y-4">
                  <div className="border-l-2 border-[#1EBFB3]/30 pl-4 space-y-4">
                    {subsection.nestedSubsections.map((nested, nestedIdx) => (
                      <div 
                        key={nestedIdx}
                        className="bg-zinc-900/50 border border-zinc-700 rounded-lg overflow-hidden"
                      >
                        {/* Nested Subsection Header */}
                        <div className="flex items-center gap-3 p-3 bg-zinc-800/50 border-b border-zinc-700">
                          <div className="w-8 h-6 rounded bg-[#1EBFB3]/30 text-[#1EBFB3] flex items-center justify-center font-bold text-xs shrink-0">
                            {idx + 1}.{nestedIdx + 1}
                          </div>
                          <Input
                            value={nested.title}
                            onChange={(e) => updateNestedSubsectionTitle(idx, nestedIdx, e.target.value)}
                            className="flex-1 bg-zinc-900 border-zinc-600 text-white text-sm focus:border-[#1EBFB3]"
                            placeholder="Sub-section title..."
                          />
                          <button
                            onClick={() => removeNestedSubsection(idx, nestedIdx)}
                            className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-zinc-700 rounded transition-colors"
                            title="Remove sub-section"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        
                        {/* Nested Content Editor */}
                        <div className="p-3">
                          <RichTextEditor
                            value={nested.content}
                            onChange={(newContent) => updateNestedSubsectionContent(idx, nestedIdx, newContent)}
                            placeholder={`Enter content for ${idx + 1}.${nestedIdx + 1}...`}
                            minHeight="120px"
                            testId={`nested-subsection-editor-${idx}-${nestedIdx}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Add Nested Subsection Button at bottom of each section */}
              {(!subsection.nestedSubsections || subsection.nestedSubsections.length === 0) && (
                <div className="px-4 pb-4">
                  <button
                    onClick={() => addNestedSubsection(idx)}
                    className="w-full flex items-center justify-center gap-2 p-2 border border-dashed border-zinc-600 text-zinc-400 hover:text-[#1EBFB3] hover:border-[#1EBFB3] rounded-lg text-sm transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Add Sub-section ({idx + 1}.1, {idx + 1}.2, ...)
                  </button>
                </div>
              )}
            </div>
          ))}
          
          <p className="text-xs text-zinc-500 mt-2">
            Tip: Use the formatting toolbar for bold, italic, underline, and lists. You can also use Ctrl+B, Ctrl+I, Ctrl+U shortcuts.
          </p>
        </div>
      ) : (
        // View Mode: Collapsible Subsections
        <>
          {/* Expand/Collapse All Controls */}
          {subsections.length > 1 && (
            <div className="flex items-center justify-end gap-2 mb-4">
              <button
                onClick={expandAll}
                className="text-xs text-zinc-400 hover:text-white flex items-center gap-1 px-2 py-1 rounded hover:bg-zinc-800"
              >
                <ChevronDown className="h-3 w-3" />
                Expand All
              </button>
              <span className="text-zinc-600">|</span>
              <button
                onClick={collapseAll}
                className="text-xs text-zinc-400 hover:text-white flex items-center gap-1 px-2 py-1 rounded hover:bg-zinc-800"
              >
                <ChevronUp className="h-3 w-3" />
                Collapse All
              </button>
            </div>
          )}

          {/* Collapsible Subsections */}
          <div className="space-y-4 mb-8">
            {subsections.map((subsection, idx) => (
              <div 
                key={idx} 
                className={`border rounded-xl overflow-hidden transition-all ${
                  expandedSubsections.has(idx) 
                    ? 'border-zinc-700 bg-zinc-800/30' 
                    : 'border-zinc-800 bg-zinc-900/50'
                }`}
              >
                {/* Subsection Header - Clickable */}
                <button
                  onClick={() => toggleSubsection(idx)}
                  className="w-full flex items-center justify-between p-4 hover:bg-zinc-800/50 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      expandedSubsections.has(idx) 
                        ? 'bg-navigate-gold text-black' 
                        : 'bg-zinc-700 text-zinc-400'
                    }`}>
                      {idx + 1}
                    </div>
                    <h3 className={`font-semibold ${
                      expandedSubsections.has(idx) ? 'text-white' : 'text-zinc-300'
                    }`}>
                      {subsection.title}
                    </h3>
                  </div>
                  {expandedSubsections.has(idx) ? (
                    <ChevronUp className="h-5 w-5 text-navigate-gold" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-zinc-500" />
                  )}
                </button>
                
                {/* Subsection Content - Expandable */}
                {expandedSubsections.has(idx) && (
                  <div className="px-4 pb-4 border-t border-zinc-700/50">
                    <div className="pt-4 prose prose-invert max-w-none">
                      <SubsectionContent content={subsection.content} />
                    </div>
                    
                    {/* Nested Sub-sections (2.1, 2.2, etc.) in View Mode */}
                    {subsection.nestedSubsections && subsection.nestedSubsections.length > 0 && (
                      <div className="mt-6 space-y-4 border-l-2 border-[#1EBFB3]/30 pl-4 ml-2">
                        {subsection.nestedSubsections.map((nested, nestedIdx) => (
                          <div key={nestedIdx} className="bg-zinc-900/30 rounded-lg p-4">
                            <div className="flex items-center gap-3 mb-3">
                              <span className="px-2 py-1 bg-[#1EBFB3]/20 text-[#1EBFB3] rounded text-xs font-bold">
                                {idx + 1}.{nestedIdx + 1}
                              </span>
                              <h4 className="text-white font-medium">{nested.title}</h4>
                            </div>
                            <div className="prose prose-invert max-w-none prose-sm">
                              <SubsectionContent content={nested.content} />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Embedded Media Section */}
      {(embeddedMedia.length > 0 || editMode) && (
        <div className="mb-6" data-testid="section-media-resources">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-[#1EBFB3]" />
              Media & Resources
            </h3>
            {editMode && (
              <button
                onClick={() => setShowEmbedMediaPicker(true)}
                className="flex items-center gap-2 px-3 py-1.5 bg-[#1EBFB3]/20 text-[#1EBFB3] border border-[#1EBFB3]/30 rounded-lg hover:bg-[#1EBFB3]/30 transition-colors text-sm"
                data-testid="add-embedded-media"
              >
                <Plus className="h-4 w-4" />
                Add Media
              </button>
            )}
          </div>
          {embeddedMedia.length > 0 ? (
            <EmbeddedMediaGrid media={embeddedMedia} editMode={editMode} resolveUrl={getImageUrl} onChange={saveEmbeddedMedia}
              onExpand={(m) => setLightboxImage({ url: getImageUrl(m.url), caption: m.caption, filename: m.filename })} />
          ) : editMode ? (
            <button type="button" onClick={() => setShowEmbedMediaPicker(true)} className="w-full text-center py-8 border-2 border-dashed border-zinc-700 hover:border-[#1EBFB3]/60 rounded-xl transition-colors" data-testid="add-embedded-media-empty">
              <ImageIcon className="h-10 w-10 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-300 mb-1">Drop in images, videos, infographics or documents</p>
              <p className="text-xs text-zinc-500">Upload from your computer or paste a YouTube / Vimeo link</p>
            </button>
          ) : null}
        </div>
      )}

      <SectionMediaUploader open={showEmbedMediaPicker} onClose={() => setShowEmbedMediaPicker(false)} onAdd={addEmbeddedMedia}
        token={token} courseId={courseId} courseTitle={courseMetadata?.title}
        renderLibrary={(onSelect) => <MediaLibraryPicker onSelect={onSelect} />} />

      {/* Image Lightbox for expanded view */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black/95 flex items-center justify-center z-[100] p-4 cursor-zoom-out"
          onClick={() => setLightboxImage(null)}
        >
          <div className="relative max-w-[95vw] max-h-[95vh]" onClick={e => e.stopPropagation()}>
            {/* Close button */}
            <button
              onClick={() => setLightboxImage(null)}
              className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              title="Close (Esc)"
            >
              <X className="h-6 w-6" />
            </button>
            
            {/* Image */}
            <img
              src={lightboxImage.url}
              alt={lightboxImage.filename || 'Expanded image'}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
            
            {/* Caption */}
            {lightboxImage.caption && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 rounded-b-lg">
                <p className="text-white text-center">{lightboxImage.caption}</p>
              </div>
            )}
            
            {/* Minimize hint */}
            <div className="absolute bottom-4 right-4 flex items-center gap-2 text-white/50 text-sm">
              <Minimize2 className="h-4 w-4" />
              Click anywhere to close
            </div>
          </div>
        </div>
      )}

      {editMode && onUpdateContent ? (
        <SavedVisualsEditor courseId={courseId} sectionIndex={sectionNumber - 1} token={token} blocks={section.visual_blocks} review={section.visual_review}
          onSaved={(blocks, review) => { onUpdateContent('visual_blocks', blocks); onUpdateContent('visual_review', review); }} />
      ) : (section.visual_review?.status !== 'pending' && <VisualBlocks blocks={section.visual_blocks} />)}
      {editMode && courseId && (
        <EnhanceVisualsButton courseId={courseId} sectionIndex={sectionNumber - 1} token={token}
          hasBlocks={!!section.visual_blocks?.length} onSaved={(blocks, review) => { onUpdateContent('visual_blocks', blocks); onUpdateContent('visual_review', review); }} />
      )}

      <SectionSources sources={section.sources} />

      {/* Key Takeaways */}
      {(section.key_takeaways?.length > 0 || editMode) && (
        <div className="bg-navigate-gold/10 border border-navigate-gold/30 rounded-xl p-6 mb-6 relative group">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-navigate-gold flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Key Takeaways
            </h3>
            {editMode && (
              <button
                onClick={() => {
                  setLocalKeyTakeaways('');
                  if (onUpdateContent) {
                    onUpdateContent('key_takeaways', []);
                  }
                  toast.success('Key Takeaways section cleared');
                }}
                className="p-2 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                title="Clear Key Takeaways"
                data-testid="delete-key-takeaways"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
          {editMode ? (
            <RichTextEditor
              value={localKeyTakeaways}
              onChange={setLocalKeyTakeaways}
              placeholder="Enter key takeaways (one per line)..."
              minHeight="120px"
              testId="key-takeaways-editor"
            />
          ) : (
            <ul className="space-y-3">
              {section.key_takeaways?.map((takeaway, idx) => (
                <li key={idx} className="flex items-start gap-3 text-zinc-300">
                  <CheckCircle2 className="h-5 w-5 text-navigate-gold shrink-0 mt-0.5" />
                  <span className="leading-relaxed">{takeaway}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Practical Examples */}
      {(section.practical_examples?.length > 0 || editMode) && (
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6 mb-8 relative group">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">Practical Examples</h3>
            {editMode && (
              <button
                onClick={() => {
                  setLocalPracticalExamples('');
                  if (onUpdateContent) {
                    onUpdateContent('practical_examples', []);
                  }
                  toast.success('Practical Examples section cleared');
                }}
                className="p-2 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                title="Clear Practical Examples"
                data-testid="delete-practical-examples"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
          {editMode ? (
            <RichTextEditor
              value={localPracticalExamples}
              onChange={setLocalPracticalExamples}
              placeholder="Enter practical examples (separate with blank lines)..."
              minHeight="150px"
              testId="practical-examples-editor"
            />
          ) : (
            <ul className="space-y-4">
              {section.practical_examples?.map((example, idx) => (
                <li key={idx} className="text-zinc-300 pl-4 border-l-2 border-zinc-600 py-2">
                  {example}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-8 border-t border-zinc-800">
        <button
          onClick={onPrev}
          className="flex items-center gap-2 px-4 py-2 text-zinc-400 hover:text-white transition-colors"
        >
          <ChevronLeft className="h-5 w-5" />
          {isFirst ? 'Overview' : 'Previous'}
        </button>
        
        <button
          onClick={onNext}
          className="flex items-center gap-2 px-6 py-3 bg-navigate-gold text-black rounded-lg font-medium hover:bg-navigate-gold-light transition-colors"
        >
          {isLast ? (noQuiz ? 'Finish Course' : 'Take Quiz') : 'Next Section'}
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

// Helper function to format inline markdown (bold, italic)
function formatInlineMarkdown(text) {
  if (!text) return text;
  
  // First normalize any escaped newlines within inline text
  let normalized = text
    .replace(/\\n/g, ' ')  // Replace escaped newlines with spaces in inline text
    .replace(/\\t/g, ' '); // Replace escaped tabs
  
  // Handle bold markers **text** - more robust regex
  let parts = normalized.split(/(\*\*[^*]+\*\*)/g);
  
  return parts.map((part, idx) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={idx} className="text-white font-semibold">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

// Subsection Content Renderer - renders markdown content beautifully
function SubsectionContent({ content }) {
  if (!content) return null;
  
  // Convert escaped newlines to actual newlines for proper parsing
  const normalizedContent = content
    .replace(/\\n\\n/g, '\n\n')  // Double escaped newlines
    .replace(/\\n/g, '\n')       // Single escaped newlines
    .replace(/\\t/g, '  ')       // Tabs to spaces
    .replace(/\r\n/g, '\n');     // Windows line endings
  
  const lines = normalizedContent.split('\n');
  const elements = [];
  let currentList = [];
  let currentListType = null;
  
  const flushList = () => {
    if (currentList.length > 0) {
      if (currentListType === 'bullet') {
        elements.push(
          <ul key={elements.length} className="space-y-2 my-4 pl-4">
            {currentList.map((item, idx) => (
              <li key={idx} className="flex items-start gap-3 text-zinc-300">
                <span className="w-1.5 h-1.5 bg-navigate-gold rounded-full mt-2.5 shrink-0" />
                <span className="leading-relaxed">{formatInlineMarkdown(item)}</span>
              </li>
            ))}
          </ul>
        );
      } else if (currentListType === 'numbered') {
        elements.push(
          <ol key={elements.length} className="space-y-2 my-4 pl-4">
            {currentList.map((item, idx) => (
              <li key={idx} className="flex items-start gap-3 text-zinc-300">
                <span className="w-5 h-5 bg-zinc-800 text-navigate-gold text-xs rounded-full flex items-center justify-center shrink-0 font-medium mt-0.5">
                  {idx + 1}
                </span>
                <span className="leading-relaxed">{formatInlineMarkdown(item)}</span>
              </li>
            ))}
          </ol>
        );
      }
      currentList = [];
      currentListType = null;
    }
  };
  
  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    
    // Skip empty lines
    if (!trimmed) {
      flushList();
      return;
    }
    
    // H3 Subheaders (### Header)
    if (trimmed.startsWith('### ')) {
      flushList();
      elements.push(
        <h4 key={elements.length} className="text-lg font-semibold text-navigate-gold mt-6 mb-3">
          {trimmed.replace(/^###\s*/, '')}
        </h4>
      );
      return;
    }
    
    // H4 Subheaders (#### Header)
    if (trimmed.startsWith('#### ')) {
      flushList();
      elements.push(
        <h5 key={elements.length} className="text-base font-semibold text-white mt-4 mb-2">
          {trimmed.replace(/^####\s*/, '')}
        </h5>
      );
      return;
    }
    
    // Bold headers like "**Header:**" or "**Header**:"
    if (trimmed.match(/^\*\*[^*]+\*\*:?\s*$/)) {
      flushList();
      const headerText = trimmed.replace(/\*\*/g, '').replace(/:$/, '');
      elements.push(
        <h5 key={elements.length} className="text-base font-semibold text-white mt-4 mb-2">
          {headerText}
        </h5>
      );
      return;
    }
    
    // Bullet points with bold start like "- **Item**: description"
    if (trimmed.match(/^[-•]\s*\*\*[^*]+\*\*:?/)) {
      if (currentListType !== 'bullet') {
        flushList();
        currentListType = 'bullet';
      }
      const cleanText = trimmed.replace(/^[-•]\s*/, '');
      currentList.push(cleanText);
      return;
    }
    
    // Regular bullet points
    if (trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
      if (currentListType !== 'bullet') {
        flushList();
        currentListType = 'bullet';
      }
      currentList.push(trimmed.replace(/^[-•]\s*/, ''));
      return;
    }
    
    // Numbered items
    if (trimmed.match(/^\d+\.\s+/)) {
      if (currentListType !== 'numbered') {
        flushList();
        currentListType = 'numbered';
      }
      currentList.push(trimmed.replace(/^\d+\.\s*/, ''));
      return;
    }
    
    // Blockquotes
    if (trimmed.startsWith('> ')) {
      flushList();
      elements.push(
        <blockquote key={elements.length} className="border-l-4 border-navigate-gold/50 bg-navigate-gold/5 pl-4 py-2 my-4 rounded-r">
          <p className="text-zinc-300 italic">{trimmed.replace(/^>\s*/, '')}</p>
        </blockquote>
      );
      return;
    }
    
    // Regular paragraph
    flushList();
    elements.push(
      <p key={elements.length} className="text-zinc-300 leading-relaxed my-3">
        {formatInlineMarkdown(trimmed)}
      </p>
    );
  });
  
  flushList();
  
  return <div className="space-y-1">{elements}</div>;
}

// Quiz Section Component
function QuizSection({ quiz, answers, onAnswer, onSubmit }) {
  // Handle both formats: {id, options: [str]} and {question_id, options: [{text}]}
  const getQuestionId = (q) => q.id || q.question_id;
  const getOptionText = (option) => typeof option === 'string' ? option : option.text;
  
  const allAnswered = quiz.every(q => answers[getQuestionId(q)] !== undefined);
  
  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-navigate-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <HelpCircle className="h-8 w-8 text-navigate-gold" />
        </div>
        <h1 className="font-heading text-3xl font-bold text-white mb-2">Final Quiz</h1>
        <p className="text-zinc-400">Test your knowledge from this course</p>
      </div>

      <div className="space-y-8">
        {quiz.map((question, qIdx) => {
          const questionId = getQuestionId(question);
          return (
            <div key={questionId} className="bg-navigate-surface border border-zinc-800 rounded-xl p-6">
              <div className="flex items-start gap-4 mb-4">
                <span className="w-8 h-8 bg-zinc-800 rounded-full flex items-center justify-center text-zinc-400 text-sm shrink-0">
                  {qIdx + 1}
                </span>
                <div>
                  {question.question_type === 'scenario' && (
                    <span className="text-xs text-[#1EBFB3] bg-[#1EBFB3]/20 px-2 py-0.5 rounded mb-2 inline-block">
                      Scenario-Based
                    </span>
                  )}
                  <p className="text-white font-medium">{question.question}</p>
                </div>
              </div>
              
              <div className="space-y-2 ml-12">
                {question.options?.map((option, optIdx) => {
                  const optionText = getOptionText(option);
                  const isSelected = answers[questionId] === optIdx;
                  return (
                    <button
                      key={optIdx}
                      onClick={() => onAnswer(questionId, optIdx)}
                      className={`w-full p-4 rounded-lg text-left transition-all border ${
                        isSelected
                          ? 'bg-navigate-gold/20 border-navigate-gold text-white'
                          : 'bg-zinc-800/50 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                      }`}
                    >
                      <span className="font-medium mr-2">{['A', 'B', 'C', 'D'][optIdx]}.</span>
                      {optionText}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-center mt-8">
        <button
          onClick={onSubmit}
          disabled={!allAnswered}
          className="px-8 py-4 bg-navigate-gold text-black rounded-xl font-medium hover:bg-navigate-gold-light transition-colors flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <CheckCircle2 className="h-5 w-5" />
          Submit Quiz
        </button>
      </div>
      
      {!allAnswered && (
        <p className="text-center text-zinc-500 mt-4 text-sm">
          Please answer all {quiz.length} questions to submit
        </p>
      )}
    </div>
  );
}

// Results Section Component
function ResultsSection({ score, total, quiz, answers, onRetry }) {
  const percentage = Math.round((score / total) * 100);
  const passed = percentage >= 70;
  
  // Helper functions to handle both data formats
  const getQuestionId = (q) => q.id || q.question_id;
  const getOptionText = (option) => typeof option === 'string' ? option : option.text;
  const getCorrectAnswer = correctIndex;
  
  return (
    <div className="p-8 max-w-4xl mx-auto">
      {/* Score Card */}
      <div className={`rounded-2xl p-8 mb-8 text-center ${
        passed 
          ? 'bg-gradient-to-r from-green-900/50 to-emerald-900/50 border border-green-500/30'
          : 'bg-gradient-to-r from-yellow-900/50 to-orange-900/50 border border-yellow-500/30'
      }`}>
        <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 ${
          passed ? 'bg-green-500/20' : 'bg-yellow-500/20'
        }`}>
          <Trophy className={`h-12 w-12 ${passed ? 'text-green-400' : 'text-yellow-400'}`} />
        </div>
        
        <h2 className="text-3xl font-bold text-white mb-2">
          {passed ? 'Congratulations!' : 'Keep Learning!'}
        </h2>
        <p className="text-xl text-zinc-300 mb-4">
          You scored <span className={`font-bold ${passed ? 'text-green-400' : 'text-yellow-400'}`}>{score}</span> out of {total}
        </p>
        <div className="text-5xl font-bold mb-4" style={{ color: passed ? '#4ade80' : '#facc15' }}>
          {percentage}%
        </div>
        <p className="text-zinc-400">
          {passed ? 'You have successfully completed this course!' : 'A score of 70% or higher is required to pass.'}
        </p>
      </div>

      {/* Review Answers */}
      <h3 className="text-xl font-bold text-white mb-4">Review Your Answers</h3>
      <div className="space-y-4 mb-8">
        {quiz.map((question, idx) => {
          const questionId = getQuestionId(question);
          const userAnswer = answers[questionId];
          const correctAnswer = getCorrectAnswer(question);
          const isCorrect = userAnswer === correctAnswer;
          
          // Get display text for answers
          const userAnswerText = question.options?.[userAnswer] 
            ? getOptionText(question.options[userAnswer])
            : userAnswer;
          const correctAnswerText = question.options?.[correctAnswer]
            ? getOptionText(question.options[correctAnswer])
            : correctAnswer;
          
          return (
            <div 
              key={questionId}
              className={`p-4 rounded-xl border ${
                isCorrect 
                  ? 'bg-green-500/10 border-green-500/30' 
                  : 'bg-red-500/10 border-red-500/30'
              }`}
            >
              <div className="flex items-start gap-3">
                {isCorrect ? (
                  <CheckCircle2 className="h-5 w-5 text-green-400 shrink-0 mt-0.5" />
                ) : (
                  <X className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className="text-white font-medium mb-2">{question.question}</p>
                  <p className={`text-sm ${isCorrect ? 'text-green-400' : 'text-red-400'}`}>
                    Your answer: {['A', 'B', 'C', 'D'][userAnswer]}. {userAnswerText}
                  </p>
                  {!isCorrect && (
                    <p className="text-sm text-green-400 mt-1">
                      Correct answer: {['A', 'B', 'C', 'D'][correctAnswer]}. {correctAnswerText}
                    </p>
                  )}
                  {question.explanation && (
                    <p className="text-sm text-zinc-400 mt-2 italic">{question.explanation}</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div className="flex justify-center gap-4">
        {!passed && (
          <button
            onClick={onRetry}
            className="px-6 py-3 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-colors"
          >
            Retry Quiz
          </button>
        )}
        <Link
          to="/dashboard"
          className="px-6 py-3 bg-navigate-gold text-black rounded-lg font-medium hover:bg-navigate-gold-light transition-colors"
        >
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
}
